'use client';
// Fiche document (design isDoc) : sous-onglets Lecture / Versions / Journal /
// Diffusion / Commentaires + zone d'impression maîtrisée + actions par rôle.
// Inclut le Circuit de revue (isReview). L'éditeur de brouillon interne a été
// retiré le 29/07 (D1) : un document se rédige dans son fichier bureautique,
// et son aperçu officiel est le PDF produit par la plateforme.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed, userName, userInitials } from '@/lib/store';
import { attendDeMoi, peutApprouverSonTexte } from '@/lib/taches-signature';
import type { GedDocument, GedDiffusionEntry, AudienceApercu, AudienceClause, DocStatut } from '@/lib/types';
import { stateMeta, STATES, fmtDate, fmtDateTime, estAffectable, monEtatLecture, PERIODICITES, jourIso, aujourdhuiIso } from '@/lib/meta';
import { mentionDe, type SituationMention } from '@/lib/mentionsCartouche';
import { StatePill, I, card, inputStyle, ConfirmButton, useEscape, useBusy, Selecteur, useConfirmation, TitrePage, EmptyState, Modale, FieldError, enChargement, Skeleton } from '@/components/ui';
import { useFormulaire } from '@/components/Formulaire';
import { OfficeTab } from '@/components/screens/OfficeTab';
import { ModaleVerification } from '@/components/ModaleVerification';
import { ChangementsVersion } from '@/components/ChangementsVersion';
import { BoutonJournal } from '@/components/BoutonJournal';
import { ApercuTab } from '@/components/screens/ApercuTab';
import {
  versionApplicable, versionCandidate, revisionEnCours, accesVersionEnCours, etapeEnCours, libelleEtape,
  accesSourceEnVigueur,
} from '@/lib/version-en-cours';
import {
  ficheCourante, ficheDeVersion, ficheAffichee, ficheArchivee, cibleFiche, ecartsFiche,
  LIBELLE_CHAMP, PHRASE_FICHE_ABSENTE, renduValeur, type Fiche,
} from '@/lib/fiche-version';
import { Liens } from '@/components/Liens';
import { useEmplacements } from '@/lib/emplacements';
import { useUrlsModules } from '@/shared/urlsModulesClient';


import { Bouton } from '@/shared/Bouton';
import { styleBouton } from '@/shared/boutonStyles';
import { PiedActions } from '@/shared/PiedActions';
import { Segment } from '@/shared/Segment';
import { IconeApercu, GestionApercu } from '@/components/ApercuAnime';
function useSelectedDoc() {
  const { docs, selectedCode } = useGed();
  return docs.find((d) => d.code === selectedCode) || null;
}

/* ═══════════════════════════════════════════════════════════════════════════
 * AUDIENCE DE DIFFUSION (lot N8 bis) — la règle se pose ici, elle part ailleurs.
 *
 * ⚠️ L'audience est une RÈGLE, pas une liste de noms. Cet écran la COMPOSE ; il
 * ne la résout jamais. La conversion en personnes est faite par le serveur
 * (`services/audience.ts`), au moment du clic de diffusion, avec l'état du jour
 * des habilitations, des postes et des effectifs. Une résolution faite ici
 * serait fausse dès le lendemain, et surtout inauditable : personne ne pourrait
 * rejouer ce que le navigateur a calculé ce jour-là.
 *
 * Ce que l'écran doit donc garantir, et rien de plus :
 *  · la règle se règle TANT QUE le document n'est pas en vigueur (le serveur
 *    refuse au-delà : ce qui a été diffusé se lit dans la liste et au journal,
 *    pas dans une intention réécrite après coup) ;
 *  · on VOIT qui sera notifié AVANT de cliquer — une règle appliquée en aveugle
 *    sur un document en vigueur n'est pas rattrapable, les accusés sont exigés
 *    et les échéances courent déjà ;
 *  · ce que la règle NE désigne pas (clause stérile, personne écartée) est dit,
 *    jamais tu : une absence inexpliquée est le premier constat d'audit.
 * ═════════════════════════════════════════════════════════════════════════ */

/* ⚠️ Les aides disent CE QUE LA CLAUSE LIT, et non ce qu'elle a l'air de dire.
   « Par poste » et « par fonction » se ressemblent à l'écran et ne désignent
   pas les mêmes gens : la fonction se lit sur le DOSSIER de la personne, le
   poste sur le PLANNING — un poste que le planning n'ouvre pas ne désigne
   personne, et la clause paraît pourtant parfaitement remplie. */
const NATURES: { type: AudienceClause['type']; label: string; aide: string }[] = [
  // Le défaut décidé par la RQ (04/10/2026) : le plus large, qu'on restreint.
  { type: 'tous', label: 'Tout le personnel du site', aide: 'Tous les comptes actifs du site.' },
  { type: 'fonction', label: 'Par fonction', aide: 'Le MÉTIER inscrit au dossier de la personne (biologiste, technicien, préleveur…), quel que soit le poste tenu ce jour-là.' },
  { type: 'poste', label: 'Par poste', aide: 'Ceux qui ont réellement TENU ce poste au planning. Un poste que le planning n’ouvre pas ne désigne personne.' },
  { type: 'habilitation', label: 'Par habilitation', aide: 'Ceux qui détiennent la compétence, à l’état du jour — une habilitation échue n’habilite plus.' },
  { type: 'nominatif', label: 'Nominativement', aide: 'Des personnes désignées. Reste une règle : résolue au clic, comme les autres — quelqu’un parti entre-temps est écarté.' },
];

/** Une clause qui ne désigne encore personne (cases vides) — refusée à l'ajout. */
const clauseIncomplete = (c: AudienceClause) =>
  (c.type === 'fonction' && !(c.ids || []).length)
  || (c.type === 'poste' && !(c.codes || []).length)
  || (c.type === 'nominatif' && !(c.userIds || []).length)
  || (c.type === 'habilitation' && (!((c.disciplines || []).length + (c.codes || []).length) || !(c.niveaux || []).length));


/**
 * Le CIRCUIT DE VIE du document, en tête de fiche — comme les NC (RQ, 19/08).
 *
 * Les états existaient (pastille), le journal aussi (fenêtre), mais rien ne
 * montrait la SÉQUENCE : où en est ce document, par où est-il passé, quand.
 * Les dates viennent du journal signé — le DERNIER franchissement de chaque
 * étape, un document révisé repassant par les mêmes (même règle que les NC,
 * apprise sur BIO24-NC-2026-016).
 *
 * Les états latéraux (refusé, abandonné, retiré d'urgence) ne sont pas des
 * étapes : ils s'affichent en travers de la frise, pas dedans.
 */
function JalonsDocument({ doc }: { doc: GedDocument }) {
  const { events, loadJournal } = useGed();
  /* Les documents EXTERNES (norme adoptée, notice enregistrée) ne suivent PAS
     le circuit interne : leur montrer une frise « Rédaction → Approbation »
     affirmerait un chemin qu'ils n'ont jamais pris — constaté à l'écran sur
     DE-DOC-001, présenté « Demandé » alors qu'il est adopté. */
  const externes = ['enregistre', 'adopte', 'remplace', 'externe'];
  useEffect(() => { void loadJournal(doc.code); }, [doc.code, loadJournal]);
  const journal = events[doc.code] || [];

  const dateEv = (...types: string[]) => {
    const trouves = journal.filter((e) => types.includes(e.evType));
    return trouves.length ? trouves[trouves.length - 1].ts : undefined;
  };

  const ORDRE: { k: DocStatut[]; label: string; date?: string; aide: string }[] = [
    { k: ['demande'], label: 'Demandé', date: dateEv('DemandeOuverte'), aide: 'Le besoin est exprimé et statué — rien n’est encore écrit.' },
    { k: ['brouillon', 'enrevision'], label: 'Rédaction', date: dateEv('BrouillonCree', 'RenvoyeBrouillon', 'RevisionDeclenchee'), aide: 'Espace privé du rédacteur, versionné v0.x. Le chargement du fichier fait partie de cette étape.' },
    { k: ['averifier'], label: 'Vérification', date: dateEv('SoumisVerification'), aide: 'Soumis, contenu figé pour revue.' },
    { k: ['aapprouver'], label: 'Approbation', date: dateEv('Verifie'), aide: 'Vérifié avec signature, en attente d’approbation.' },
    { k: ['envigueur', 'obsolete'], label: 'En vigueur', date: dateEv('Approuve', 'MisEnVigueur', 'DiffusionEmise'), aide: 'Approuvé et diffusé : version applicable unique, accusés en cours.' },
  ];
  const lateral = ['refuse', 'abandonne', 'retire'].includes(doc.statut);
  if (externes.includes(doc.statut)) return null;
  const courant = lateral ? -1 : Math.max(0, ORDRE.findIndex((j) => j.k.includes(doc.statut)));

  return (
    <div style={{ margin: '14px 0 4px' }}>
      {lateral && (
        <div style={{ fontSize: 12.5, color: 'var(--red-600)', marginBottom: 6 }}>
          Circuit interrompu — {STATES[doc.statut]?.label?.toLowerCase() || doc.statut}. La frise montre le chemin parcouru avant l’arrêt.
        </div>
      )}
      <ol style={{ display: 'grid', gridTemplateColumns: `repeat(${ORDRE.length},1fr)`, gap: 0, listStyle: 'none', margin: 0, padding: 0 }}>
        {ORDRE.map((j, i) => {
          const franchi = lateral ? !!j.date : i <= courant;
          const actuel = !lateral && i === courant;
          return (
            <li key={j.label} title={j.aide} aria-current={actuel ? 'step' : undefined}
              style={{ display: 'grid', gap: 5, cursor: 'help', minWidth: 0 }}>
              {/* Hauteur figée — même correction que la frise des NC (13/08) :
                  sans elle, le fil se décale d’une étape à l’autre. */}
              <div style={{ display: 'flex', alignItems: 'center', height: 14 }}>
                <span style={{ flex: 1, height: 2, background: i === 0 ? 'transparent' : (franchi ? 'var(--color-primary)' : 'var(--border-default)') }} />
                <span style={{
                  width: actuel ? 13 : 10, height: actuel ? 13 : 10, borderRadius: '50%', flex: 'none',
                  background: franchi ? 'var(--color-primary)' : 'var(--surface-card)',
                  border: `2px solid ${franchi ? 'var(--color-primary)' : 'var(--border-strong)'}`,
                }} />
                <span style={{ flex: 1, height: 2, background: i === ORDRE.length - 1 ? 'transparent' : (i < courant ? 'var(--color-primary)' : 'var(--border-default)') }} />
              </div>
              <div style={{ textAlign: 'center', fontSize: 12.5, fontWeight: actuel ? 700 : 600, color: franchi ? 'var(--text-strong)' : 'var(--text-muted)' }}>{j.label}</div>
              <div style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-faint)' }}>
                {j.date ? fmtDate(j.date) : '—'}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Rendu littéral d'une clause — même phrase qu'au journal signé, côté serveur. */
function libelleClause(c: AudienceClause, ap: AudienceApercu | null): string {
  const nomF = (id: string) => ap?.choix.fonctions.find((f) => f.id === id)?.label || id;
  const nomP = (code: string) => ap?.choix.postes.find((p) => p.code === code)?.libelle || code;
  switch (c.type) {
    case 'tous': return 'Tout le personnel';
    case 'fonction': return `Fonction : ${(c.ids || []).map(nomF).join(', ')}`;
    case 'poste': return `Poste : ${(c.codes || []).map(nomP).join(', ')}`;
    case 'habilitation': return `Habilitation ${[...(c.disciplines || []), ...(c.codes || [])].join(', ')} — ${(c.niveaux || []).join(', ')}`;
    case 'nominatif': return `Nominatif : ${(c.userIds || []).length} personne(s)`;
    default: return c.type;
  }
}

/** Cases à cocher d'une clause : la liste des valeurs possibles, telle que le
 *  SERVEUR l'a servie avec l'aperçu. L'écran n'invente aucune valeur. */
function Cases({ options, choisis, onChange }: {
  options: { valeur: string; label: string }[];
  choisis: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4, maxHeight: 190, overflowY: 'auto' }}>
      {options.map((o) => (
        <label key={o.valeur} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, cursor: 'pointer' }}>
          <input type="checkbox" checked={choisis.includes(o.valeur)}
            onChange={(e) => onChange(e.target.checked ? [...choisis, o.valeur] : choisis.filter((x) => x !== o.valeur))} />
          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
        </label>
      ))}
      {!options.length && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucune valeur disponible.</span>}
    </div>
  );
}

function AudiencePanel({ doc }: { doc: GedDocument }) {
  const { lireAudience, enregistrerAudience, apercuAudienceBrouillon, act, session } = useGed();
  const [ap, setAp] = useState<AudienceApercu | null>(null);
  const [charge, setCharge] = useState(false);
  const [edition, setEdition] = useState(false);
  const [clauses, setClauses] = useState<AudienceClause[]>([]);
  const [requis, setRequis] = useState(true);
  const [nature, setNature] = useState<AudienceClause['type']>('tous');
  const [brouillon, setBrouillon] = useState<AudienceClause>({ type: 'tous' });
  /* APERÇU EN DIRECT (RQ, 04/10/2026) : la liste des personnes suit chaque
     clic, AVANT d'enregistrer. La règle en cours = les clauses ajoutées + la
     clause qu'on est en train de composer, si elle désigne déjà quelqu'un.
     Calculée par le serveur, comme à la diffusion — l'écran n'invente rien. */
  type ApercuDirect = Pick<AudienceApercu, 'designes' | 'ecartes' | 'steriles' | 'nouveaux'> & { erreur: string };
  const [direct, setDirect] = useState<ApercuDirect | null>(null);
  const [directEnCours, setDirectEnCours] = useState(false);
  const { run, estOccupe } = useBusy();

  const peutRegler = session.roles.includes('rq') || session.roles.includes('redacteur');

  // `doc.rev` en dépendance : toute écriture sur le document l'incrémente, donc
  // l'aperçu se recalcule après une diffusion ou un enregistrement de règle —
  // sans quoi l'écran continuerait d'afficher l'état d'avant le clic.
  useEffect(() => {
    let vivant = true;
    void lireAudience(doc.documentId).then((d) => {
      if (!vivant) return;
      setAp(d);
      setCharge(true);
      if (d?.audience) { setClauses(d.audience.clauses); setRequis(d.audience.requis); }
    });
    return () => { vivant = false; };
  }, [doc.documentId, doc.rev, lireAudience]);

  // La règle telle qu'elle partirait si l'on enregistrait maintenant.
  // « Tout le personnel » n'entre d'office que dans une règle VIDE : rouvrir
  // une règle existante ne doit pas l'élargir sans qu'on l'ait demandé.
  const brouillonUtile = !clauseIncomplete({ ...brouillon, type: nature })
    && (nature !== 'tous' || !clauses.length)
    && !clauses.some((c) => JSON.stringify(c) === JSON.stringify({ ...brouillon, type: nature }));
  const regleEnCours: AudienceClause[] = brouillonUtile ? [...clauses, { ...brouillon, type: nature }] : clauses;
  const cleDirect = edition ? JSON.stringify({ regleEnCours, requis }) : '';
  useEffect(() => {
    if (!edition) return;
    let vivant = true;
    /* ⚠️ `setDirectEnCours(true)` ÉTAIT ICI, en tête d'effet — donc exécuté de
       façon synchrone au rendu, ce que React 19 lit comme un rendu en cascade.
       C'était la seule ERREUR de lint du module. Même correction que dans
       `OfficeTab.tsx` (« `setErr(null)` était en tête d'effet ») : l'appel entre
       dans la TENTATIVE au lieu de la précéder.

       Il y gagne aussi en justesse. L'attente s'affichait à CHAQUE frappe,
       pendant les 250 ms d'apaisement où rien ne partait : un indicateur
       d'activité qui clignote sans qu'aucune requête ne circule. Elle s'affiche
       désormais quand la requête part réellement. */
    const t = setTimeout(() => {
      if (!vivant) return;
      setDirectEnCours(true);
      void apercuAudienceBrouillon(doc.documentId, { clauses: regleEnCours, requis }).then((d) => {
        if (!vivant) return;
        setDirect(d);
        setDirectEnCours(false);
      });
    }, 250);
    return () => { vivant = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleDirect, doc.documentId, apercuAudienceBrouillon]);

  const ajouter = () => {
    const c = { ...brouillon, type: nature };
    // On refuse ICI ce que le serveur refuserait de toute façon, mais en le
    // disant avant l'appel : une clause vide ne désigne personne, et une règle
    // stérile se lit à l'écran comme une règle qui marche.
    if (clauseIncomplete(c)) { useGed.getState().showToast('Cette clause ne désigne personne : complétez-la.', 'erreur'); return; }
    setClauses([...clauses, c]);
    setBrouillon({ type: nature });
  };
  const choisirNature = (t: AudienceClause['type']) => {
    setNature(t);
    setBrouillon({ type: t, ...(t === 'habilitation' ? { disciplines: ap?.discipline ? [ap.discipline] : [], niveaux: ['habilite'] } : {}) });
  };

  const enregistrer = () => run('save', async () => {
    const ok = await enregistrerAudience(doc.documentId, regleEnCours.length ? { clauses: regleEnCours, requis } : null);
    if (ok) { setEdition(false); setClauses(regleEnCours); setBrouillon({ type: nature }); }
  });
  // Choisir et envoyer dans le même geste : la règle est enregistrée (et
  // journalisée), PUIS la diffusion la résout au clic, comme toujours.
  const enregistrerEtDiffuser = () => run('diff', async () => {
    const ok = await enregistrerAudience(doc.documentId, { clauses: regleEnCours, requis });
    if (!ok) return;
    setEdition(false); setClauses(regleEnCours);
    await act(doc.documentId, 'diffuse', { selonAudience: true });
  });

  const diffuser = () => run('diff', async () => { await act(doc.documentId, 'diffuse', { selonAudience: true }); });

  if (!charge) return <div style={{ padding: '14px 20px', fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture de l’audience…</div>;

  const regle = ap?.audience || null;
  const enVigueur = doc.statut === 'envigueur';

  return (
    <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '14px 20px', display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
          À qui ce document partira
        </span>
        {peutRegler && ap?.modifiable && !edition && (
          <Bouton taille="sm" onClick={() => setEdition(true)}>
            <I n="pen" s={12} />{regle ? 'Modifier la règle' : 'Définir la règle'}
          </Bouton>
        )}
      </div>

      {/* PÉRIMÈTRE ≠ AUDIENCE (RQ, 04/10/2026). Le périmètre dit OÙ le
          document s'applique (le site, dans les métadonnées) ; l'audience dit
          QUI le reçoit et doit accuser la lecture, et se choisit ici. */}
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        Périmètre : <strong style={{ color: 'var(--text-body)' }}>{ap?.perimetre || '—'}</strong>.
        {' '}L’audience retient des <strong>critères</strong>, pas des noms : la liste est recalculée au clic de diffusion,
        sur l’état du jour des postes, des habilitations et des effectifs. Une fois diffusée, elle ne se rattrape pas :
        les accusés sont exigés, les échéances courent.
      </p>

      {!regle && !edition && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          Aucune règle n’est définie. Par défaut, elle désigne <strong>tout le personnel du site</strong> :
          « Définir la règle » pour la confirmer ou la restreindre.
        </div>
      )}

      {/* ── La règle telle qu'elle est stockée ──────────────────────────── */}
      {regle && !edition && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {regle.clauses.map((c, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--cyan-100)', color: 'var(--cyan-700)', borderRadius: 'var(--radius-pill)', padding: '4px 11px', fontSize: 12.5, fontWeight: 600 }}>
              <I n="users" s={12} />{libelleClause(c, ap)}
            </span>
          ))}
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', alignSelf: 'center' }}>
            · {regle.requis ? 'accusé de lecture requis' : 'pour information'}
          </span>
        </div>
      )}

      {/* ── UNE RÈGLE QUE PERSONNE N'A ÉCRITE ──────────────────────────────
          Vingt-six des quatre-vingts documents portent une règle produite par
          la reprise automatique du périmètre écrit : personne ne l'a composée
          à l'écran, et on ne reconnaît pas ce qu'on n'a pas choisi. Le dire en
          suffixe de ligne (« · reprise du périmètre écrit ») ne suffisait pas —
          c'est justement l'information qui manque pour comprendre d'où sortent
          ces clauses. */}
      {regle && !edition && regle.origine === 'reprise-perimetre' && (
        <div role="note" style={{ fontSize: 12.5, color: 'var(--text-body)', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '8px 11px', lineHeight: 1.45 }}>
          <strong style={{ color: 'var(--text-muted)' }}>Traduite automatiquement</strong> du périmètre écrit — personne ne
          l’a composée à l’écran. À relire avant la première diffusion : ce que la traduction a compris se lit dans les
          clauses ci-dessus, ce qu’elle n’a pas su traduire est signalé plus bas.
        </div>
      )}

      {/* Ce que la reprise n'a pas su convertir : dit, jamais deviné. */}
      {regle?.nonRepris?.length ? (
        <div role="note" style={{ fontSize: 12.5, background: 'var(--amber-50)', color: 'var(--amber-600)', borderRadius: 'var(--radius-md)', padding: '8px 11px', lineHeight: 1.45 }}>
          Non repris du périmètre écrit : <strong>{regle.nonRepris.join(' · ')}</strong> — à traduire en clause, ou à laisser de côté sciemment.
        </div>
      ) : null}

      {/* ── L'éditeur de règle ──────────────────────────────────────────── */}
      {edition && (
        <div style={{ display: 'grid', gap: 10, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>Qui reçoit ce document</span>
            <select value={nature} onChange={(e) => choisirNature(e.target.value as AudienceClause['type'])} style={inputStyle}>
              {NATURES.map((n) => <option key={n.type} value={n.type}>{n.label}</option>)}
            </select>
          </label>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
            {NATURES.find((n) => n.type === nature)?.aide}
            {nature === 'poste' && ap ? ` Fenêtre retenue : ${ap.choix.fenetrePosteJours} jours de part et d’autre d’aujourd’hui.` : ''}
          </div>

          {nature === 'fonction' && (
            <Cases options={(ap?.choix.fonctions || []).map((f) => ({ valeur: f.id, label: f.label }))}
              choisis={brouillon.ids || []} onChange={(v) => setBrouillon({ ...brouillon, ids: v })} />
          )}
          {nature === 'poste' && (
            <Cases options={(ap?.choix.postes || []).map((p) => ({ valeur: p.code, label: p.libelle }))}
              choisis={brouillon.codes || []} onChange={(v) => setBrouillon({ ...brouillon, codes: v })} />
          )}
          {nature === 'nominatif' && (
            <Cases options={(ap?.choix.personnes || []).map((p) => ({ valeur: p.userId, label: p.nom }))}
              choisis={brouillon.userIds || []} onChange={(v) => setBrouillon({ ...brouillon, userIds: v })} />
          )}
          {nature === 'habilitation' && (
            <div style={{ display: 'grid', gap: 8 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>Disciplines</div>
              <Cases options={(ap?.choix.disciplines || []).map((d) => ({ valeur: d, label: d }))}
                choisis={brouillon.disciplines || []} onChange={(v) => setBrouillon({ ...brouillon, disciplines: v })} />
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>Habilitations du référentiel</div>
              <Cases options={(ap?.choix.habilitations || []).map((h) => ({ valeur: h.code, label: h.libelle }))}
                choisis={brouillon.codes || []} onChange={(v) => setBrouillon({ ...brouillon, codes: v })} />
              <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>Niveaux retenus</div>
              {/* « formation » n'est PAS une habilitation, mais quelqu'un en
                  formation doit lire le document qu'il apprend à appliquer —
                  avec le délai allongé que le serveur lui pose. */}
              <Cases options={['habilite', 'formation', 'routine', 'expert'].map((n) => ({ valeur: n, label: n }))}
                choisis={brouillon.niveaux || []} onChange={(v) => setBrouillon({ ...brouillon, niveaux: v })} />
            </div>
          )}

          <div>
            <Bouton taille="sm" onClick={ajouter}><I n="plus" s={12} />Ajouter cette clause</Bouton>
          </div>

          {clauses.length > 0 && (
            <div style={{ display: 'grid', gap: 4 }}>
              {clauses.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, background: 'var(--surface-raised)', borderRadius: 'var(--radius-md)', padding: '6px 10px' }}>
                  <I n="users" s={12} />
                  <span style={{ flex: 1, minWidth: 0 }}>{libelleClause(c, ap)}</span>
                  <Bouton taille="sm" onClick={() => setClauses(clauses.filter((_, k) => k !== i))} title="Retirer cette clause"><I n="x" s={12} /></Bouton>
                </div>
              ))}
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
            <input type="checkbox" checked={requis} onChange={(e) => setRequis(e.target.checked)} />
            Accusé de lecture requis
          </label>

          {/* ── QUI, EN DIRECT ─────────────────────────────────────────────
              Recalculé par le serveur à chaque clic, avant tout
              enregistrement : on voit la liste bouger en cochant. */}
          <div aria-live="polite" style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
              {!regleEnCours.length
                ? 'Aucun critère : cette règle ne désigne personne.'
                : direct?.erreur
                  ? <span style={{ color: 'var(--amber-600)' }}>{direct.erreur}</span>
                  : <>Désigne <strong>{direct?.designes.length ?? '…'}</strong> personne(s) aujourd’hui
                    {direct && direct.designes.length ? ` — dont ${direct.nouveaux.length} pas encore destinataire(s)` : ''}
                    {directEnCours ? ' · mise à jour…' : ''}</>}
            </div>
            {!!regleEnCours.length && !!direct?.designes.length && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, maxHeight: 160, overflowY: 'auto' }}>
                {direct.designes.map((d) => (
                  <span key={d.userId} title={d.raisons.join(' ; ')} style={{ fontSize: 12.5, background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-pill)', padding: '2px 9px', color: 'var(--text-strong)' }}>
                    {d.nom}
                  </span>
                ))}
              </div>
            )}
            {direct?.ecartes.length ? (
              <div style={{ fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.45 }}>
                Écarté{direct.ecartes.length > 1 ? 's' : ''} : {direct.ecartes.map((e) => `${e.nom} (${e.motif})`).join(' · ')}
              </div>
            ) : null}
            {direct?.steriles.length ? (
              <div style={{ fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.45 }}>
                Sans effet aujourd’hui : {direct.steriles.join(' · ')}
              </div>
            ) : null}
          </div>

          {/* Une seule action pleine : « Enregistrer et diffuser » quand le RQ peut diffuser (le document est
              en vigueur), sinon « Enregistrer ». Dans le premier cas « Enregistrer » seul devient l'alternative. */}
          {(() => {
            const libelleEnregistrer = regleEnCours.length ? 'Enregistrer' : 'Retirer la règle';
            const peutDiffuser = enVigueur && session.roles.includes('rq');
            return (
              <PiedActions
                taille="sm"
                annuler={{ onClick: () => { setEdition(false); setClauses(regle?.clauses || []); setRequis(regle?.requis !== false); setNature('tous'); setBrouillon({ type: 'tous' }); } }}
                secondaires={peutDiffuser ? [{ libelle: libelleEnregistrer, onClick: enregistrer, disabled: estOccupe('save') }] : []}
                principale={peutDiffuser
                  ? { libelle: `Enregistrer et diffuser (${direct?.nouveaux.length ?? 0} nouveau(x))`, onClick: enregistrerEtDiffuser, disabled: estOccupe('diff') || !regleEnCours.length || !direct?.designes.length }
                  : { libelle: libelleEnregistrer, onClick: enregistrer, disabled: estOccupe('save') }}
              />
            );
          })()}
        </div>
      )}

      {/* ── Prévisualisation : QUI serait notifié, avant le clic ─────────── */}
      {regle && !edition && (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
            Cette règle désigne <strong>{ap?.designes.length ?? 0}</strong> personne(s) aujourd’hui
            {ap?.nouveaux.length ? ` — dont ${ap.nouveaux.length} qui ne sont pas encore destinataires` : ' — toutes déjà destinataires'}.
          </div>
          <div style={{ display: 'grid', gap: 3, maxHeight: 220, overflowY: 'auto' }}>
            {(ap?.designes || []).map((d) => (
              <div key={d.userId} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12.5, padding: '3px 0' }}>
                <span style={{ fontWeight: 600, color: 'var(--text-strong)' }}>{d.nom}</span>
                {ap?.nouveaux.includes(d.userId)
                  ? <span style={{ fontSize: 12.5, color: 'var(--green-600)', fontWeight: 700 }}>nouveau</span>
                  : <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>déjà destinataire</span>}
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)', minWidth: 0 }}>· {d.raisons.join(' ; ')}</span>
              </div>
            ))}
            {!ap?.designes.length && (
              <div style={{ fontSize: 12.5, color: 'var(--red-600)', fontWeight: 600 }}>
                La règle ne désigne personne aujourd’hui — la diffusion serait refusée.
              </div>
            )}
          </div>

          {/* Écartés et clauses stériles : une absence inexpliquée est le
              premier constat d'audit. */}
          {ap?.ecartes.length ? (
            <div style={{ fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.45 }}>
              Écarté{ap.ecartes.length > 1 ? 's' : ''} : {ap.ecartes.map((e) => `${e.nom} (${e.motif})`).join(' · ')}
            </div>
          ) : null}
          {ap?.steriles.length ? (
            <div style={{ fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.45 }}>
              Clause{ap.steriles.length > 1 ? 's' : ''} sans effet aujourd’hui : {ap.steriles.join(' · ')}
            </div>
          ) : null}

          {session.roles.includes('rq') && (
            enVigueur ? (
              <div>
                <Bouton variante="primaire" taille="sm" onClick={diffuser} disabled={estOccupe('diff') || !ap?.designes.length}>
                  <I n="send" s={12} />Diffuser selon l’audience ({ap?.nouveaux.length ?? 0} nouveau(x))
                </Bouton>
              </div>
            ) : (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                La diffusion ne s’enclenche qu’une fois le document <strong>en vigueur</strong> — celui-ci est « {stateMeta(doc.statut).label} ».
                Aucun accusé n’est exigé, aucune échéance ne court.
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

/* Ajout RQ de destinataires à la liste de diffusion. Le serveur n'accepte la
   diffusion que d'un document EN VIGUEUR (un document en circuit ou retiré ne
   doit jamais partir aux lecteurs) : on l'explique ici plutôt que de laisser
   l'appel échouer.

   ── GED-3, tranché le 07/08/2026 : NI pré-cocher, NI exclure ───────────────

   La demande d'origine était de PRÉ-COCHER les destinataires déjà servis ; ce
   composant les EXCLUAIT ; l'écran Diffusion ne faisait ni l'un ni l'autre. Les
   trois comportements sont abandonnés au profit d'un quatrième — celui que
   l'aperçu d'audience, dix lignes plus haut dans ce même fichier, tient déjà :
   **tout le monde est affiché, chacun porte son état, et le compte ne compte
   que ce que le clic ajoute.**

   Pourquoi ni l'un ni l'autre, en regardant ce que chacun produit :

    · PRÉ-COCHER ment sur l'effet du clic. Le serveur FUSIONNE (`ged.ts` :
      « un destinataire déjà présent garde SON échéance ») : ré-envoyer à
      quelqu'un de déjà servi est un no-op. Une case cochée annonce pourtant une
      action, et « Diffuser à 12 » sur 3 ajouts réels est un compte faux dans un
      écran dont toute la fonction est de tenir un compte juste.

    · EXCLURE ne ment pas, mais TAIT. Le RQ voyait trois noms sans savoir si les
      neuf autres étaient déjà servis, hors périmètre, ou sans compte. Sur la
      liste vide, il ne restait qu'un « Tout le personnel est déjà destinataire »
      — la bonne information, mais seulement dans le cas où elle n'a plus
      d'usage.

    · ⚠️ Et surtout : « déjà servi » N'EST PAS un état de la personne. C'est
      l'état d'une LIGNE d'une diffusion émise sur CE document — avec son
      échéance propre et son accusé. Le montrer par une case (cochée ou absente)
      écrase cette distinction ; le montrer par une mention à côté du nom la
      préserve, et permet de dire ce que la case ne pourrait pas : reçu le tant,
      accusé ou en attente.

   L'écran Diffusion (« Émettre une diffusion ») tient désormais exactement le
   même langage, avec le même composant. */

/** Une personne devant la liste de diffusion d'UN document : soit elle n'y est
 *  pas, soit elle y est — et alors on dit dans quel état. */
function EtatDestinataire({ e }: { e: GedDiffusionEntry }) {
  const lu = !!e.dateAck;
  return (
    <span title={lu
      ? `Accusé de lecture donné le ${fmtDate(e.dateAck)} — la version diffusée lui est opposable.`
      : e.requis
        ? `En attente d’accusé${e.echeance ? `, échéance au ${fmtDate(e.echeance)}` : ''}${e.relances ? ` — ${e.relances} relance(s)` : ''}. Le rediffuser ne repousserait pas cette échéance : le serveur fusionne, il ne réémet pas.`
        : 'Destinataire pour information : aucun accusé n’est exigé de lui.'}
      style={{
        fontSize: 11, fontWeight: 600, borderRadius: 'var(--radius-pill)', padding: '1px 8px', cursor: 'help',
        background: lu ? 'var(--green-50)' : e.requis ? 'var(--amber-50)' : 'var(--gray-50)',
        color: lu ? 'var(--green-600)' : e.requis ? 'var(--amber-600)' : 'var(--text-muted)',
      }}>
      {lu ? 'déjà destinataire · lu' : e.requis ? 'déjà destinataire · en attente' : 'déjà destinataire · pour information'}
    </span>
  );
}

function DiffusionAdder({ doc }: { doc: GedDocument }) {
  const { settings, act } = useGed();
  const [open, setOpen] = useState(false);
  const [choix, setChoix] = useState<string[]>([]);
  const [requis, setRequis] = useState(true);
  const [busy, setBusy] = useState(false);
  useEscape(open && !busy, () => setOpen(false));

  const parUser = new Map((doc.diffusion || []).map((x) => [x.userId, x]));
  const tous = settings?.gedUsers || [];
  // AJOUTABLES ≠ AFFICHÉS. La liste montre tout le monde ; seuls ceux qui ne
  // sont pas encore destinataires sont cochables, et eux seuls comptent.
  const candidats = tous.filter((u) => !parUser.has(u.id));
  const enVigueur = doc.statut === 'envigueur';

  // Habilitation de chacun SUR L'AXE DU DOCUMENT : c'est la question qui décide
  // de la diffusion, pas l'habilitation en général.
  //
  // L'axe, c'est la FAMILLE BIOLOGIQUE si le document en porte une, sinon son
  // PROCESSUS — le même ordre de lecture que le verrou de signature au serveur
  // (`controleHabilitation`). Deux ordres différents pour une même question
  // donneraient une liste de destinataires qui ne recoupe pas la liste des
  // signataires possibles, sans que rien ne le signale.
  const axe = doc.discipline || doc.processus || '';
  const niveau = (userId: string) => String((settings?.habilitations?.[userId] || {})[axe] || '');
  const parHabilitation = candidats.filter((u) => niveau(u.id) === 'habilite').map((u) => u.id);
  const enFormation = candidats.filter((u) => niveau(u.id) === 'formation').map((u) => u.id);
  const unir = (a: string[], b: string[]) => Array.from(new Set([...a, ...b]));

  const save = async () => {
    setBusy(true);
    const ok = await act(doc.documentId, 'diffuse', { readers: choix.map((userId) => ({ userId, requis })) });
    setBusy(false);
    if (ok) { setChoix([]); setOpen(false); }
  };

  if (!enVigueur) {
    return (
      <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
        La diffusion n’est possible que sur un document <strong>en vigueur</strong> — celui-ci est « {stateMeta(doc.statut).label} ».
      </div>
    );
  }
  if (!open) {
    return (
      <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-subtle)' }}>
        <Bouton taille="sm" onClick={() => setOpen(true)} disabled={!candidats.length}>
          <I n="diff" s={12} />Ajouter des destinataires
        </Bouton>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', marginInlineStart: 10 }}>
          {candidats.length
            ? `${parUser.size} déjà destinataire(s) sur ${tous.length} · ${candidats.length} à ajouter`
            : 'Tout le personnel est déjà destinataire.'}
        </span>
      </div>
    );
  }
  return (
    <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border-subtle)', display: 'grid', gap: 10 }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>Nouveaux destinataires</div>

      {/* Cocher vingt personnes une par une pour une procédure de bactériologie,
          c'est recopier à la main une information que l'application détient
          déjà : la matrice d'habilitations. On désigne donc la COMPÉTENCE, et
          la liste nominative se déduit — mais elle reste affichée et modifiable
          avant l'émission : c'est le RQ qui diffuse, pas la matrice. */}
      {parHabilitation.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '9px 12px' }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
            Habilités en <strong>{axe}</strong> :
          </span>
          <Bouton taille="sm" onClick={() => setChoix(unir(choix, parHabilitation))}>
            Sélectionner les {parHabilitation.length} habilité{parHabilitation.length > 1 ? 's' : ''}
          </Bouton>
          {enFormation.length > 0 && (
            <Bouton taille="sm" onClick={() => setChoix(unir(choix, enFormation))}>
              + les {enFormation.length} en formation
            </Bouton>
          )}
        </div>
      )}

      {/* TOUT LE MONDE est affiché — voir l'arbitrage GED-3 en tête du
          composant. Les déjà servis ne sont pas cochables (le serveur fusionne :
          les cocher ne ferait rien) mais ils sont là, avec leur état. */}
      <div style={{ display: 'grid', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
        {tous.map((u, i) => {
          const deja = parUser.get(u.id);
          return (
            <label key={u.id} style={{
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5,
              cursor: deja ? 'default' : 'pointer', opacity: deja ? 0.7 : 1,
            }}>
              {/* Le panneau s'ouvrait sans déplacer le focus : au clavier, celui-ci
                  restait sur le bouton qui venait d'être démonté. */}
              <input type="checkbox" autoFocus={i === 0} disabled={!!deja}
                checked={choix.includes(u.id)}
                onChange={(e) => setChoix(e.target.checked ? [...choix, u.id] : choix.filter((k) => k !== u.id))} />
              {u.nom}
              {u.fonction && <span style={{ color: 'var(--text-muted)', fontSize: 12.5 }}>· {u.fonction}</span>}
              <span style={{ flex: 1 }} />
              {deja && <EtatDestinataire e={deja} />}
            </label>
          );
        })}
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, cursor: 'pointer' }}>
        <input type="checkbox" checked={requis} onChange={(e) => setRequis(e.target.checked)} />
        Accusé de lecture requis
      </label>
      {/* Le compte ne compte que les AJOUTS : c'est la seule chose que le clic produit. */}
      <PiedActions
        taille="sm"
        annuler={{ onClick: () => { setChoix([]); setOpen(false); } }}
        principale={{ libelle: `Ajouter ${choix.length || ''} destinataire${choix.length > 1 ? 's' : ''}`.replace('  ', ' '), onClick: () => void save(), disabled: busy || !choix.length }}
      />
    </div>
  );
}

/**
 * Registre des exemplaires PAPIER (copies maîtrisées).
 *
 * Le PDF qu'on télécharge porte « COPIE NON MAÎTRISÉE » : juste au moment où il
 * sort, faux dès la version suivante. C'est le bon défaut pour une consultation,
 * pas pour la feuille punaisée au-dessus d'une paillasse — celle-là, personne ne
 * pensera à la remplacer si nul ne sait qu'elle existe.
 *
 * Déclarer un exemplaire, c'est donc accepter d'avoir à aller le récupérer. Le
 * numéro et le lieu s'impriment sur la feuille ; le centre de tâches réclame son
 * remplacement dès qu'une nouvelle version entre en vigueur.
 */
function CopiesMaitrisees({ doc, version }: {
  doc: GedDocument;
  /** La version AFFICHÉE : on montre les exemplaires émis POUR elle. */
  version: string;
}) {
  const { copies, settings, session, smq } = useGed();
  const estRQ = session.roles.includes('rq');
  // Émettre et imprimer : RQ ou approbateur (décision RQ, 04/10/2026) ; le
  // RETRAIT reste au RQ — même règle que ROLES_COPIE_MAITRISEE côté serveur.
  const peutEmettre = estRQ || session.roles.includes('approbateur');
  const [lieu, setLieu] = useState('');
  // Lot N5-3 — l'emplacement du plan, en plus du lieu écrit. FACULTATIF :
  // exiger un code obligerait à décrire tout le laboratoire avant de pouvoir
  // afficher la première feuille. Quand il est posé, l'écran du plan sait
  // répondre à « quels documents sont affichés ici ? ».
  const [emplacementCode, setEmplacementCode] = useState('');
  const { proposables: emplacements, chemin } = useEmplacements();
  const { run, estOccupe } = useBusy();

  /* ── LES EXEMPLAIRES DE LA VERSION LUE (RQ, 10/10/2026) ──────────────────
     Chaque exemplaire papier porte déjà SA version (`ged-copie.version`) : la
     donnée était là, le panneau les mélangeait. En consultant la v1.0 on
     voyait les exemplaires émis pour la v2.0 — et l'inverse. On ne liste donc
     que ceux de la version affichée ; sur la version applicable, la liste est
     celle du document entier, inchangée. */
  const estLaApplicable = !version || version === String(doc.versionCourante);
  const miennes = copies.filter((c) => c.docCode === doc.code
    && (estLaApplicable || String(c.version || '') === version));
  const enService = miennes.filter((c) => c.statut !== 'retiree');
  const perimees = enService.filter((c) => c.version !== doc.versionCourante);

  const emettre = () => run('emettre', async () => {
    if (!lieu.trim()) return;
    const ok = await smq(`ged/docs/${doc.documentId}/copie`,
      { lieu: lieu.trim(), emplacementCode }, 'Exemplaire enregistré au registre.');
    if (ok) { setLieu(''); setEmplacementCode(''); }
  });

  /* On n'émet un exemplaire QUE de ce qui s'applique : le serveur refuse déjà
     l'impression d'une archive ou d'un chantier (`etat.imprimable`), et offrir
     le formulaire ici aurait promis un tirage qu'il refuse après le clic. */
  const peutEmettreIci = peutEmettre && estLaApplicable;
  if (!peutEmettreIci && !miennes.length) return null;

  return (
    <div style={{ ...card, padding: '16px 18px' }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
        Copies papier{estLaApplicable ? '' : ` — v${version}`} ({enService.length} en service)
      </h3>
      <p style={{ margin: '0 0 12px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
        Exemplaires affichés dans le laboratoire. Un exemplaire imprimé ne se met pas à jour : il doit être remplacé à chaque nouvelle version.
      </p>

      {perimees.length > 0 && (
        <div role="alert" style={{ marginBottom: 12, background: 'var(--red-50)', color: 'var(--red-600)', borderRadius: 'var(--radius-md)', padding: '9px 12px', fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}>
          {perimees.length} exemplaire{perimees.length > 1 ? 's affichés énoncent' : ' affiché énonce'} une version périmée — à remplacer sur place.
        </div>
      )}

      {miennes.map((c) => {
        const perimee = c.statut !== 'retiree' && c.version !== doc.versionCourante;
        return (
          <div key={c.documentId} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 0', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-strong)', flex: 'none' }}>n° {c.numero}</span>
            <span style={{ flex: '1 1 120px', minWidth: 0, color: 'var(--text-body)' }}>
              {c.lieu}
              {/* Le chemin du plan sous le lieu écrit, quand il est rattaché.
                  Les deux se complètent : le plan situe la pièce, le texte
                  libre dit où exactement dans la pièce. */}
              {!!c.emplacementCode && (
                <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-muted)' }}>
                  <I n="pin" s={10} /> {chemin(c.emplacementCode)}
                </span>
              )}
            </span>
            <span style={{ fontSize: 12.5, color: perimee ? 'var(--red-600)' : 'var(--text-muted)', fontWeight: perimee ? 700 : 400 }}>
              v{c.version}{perimee ? ` — v${doc.versionCourante} en vigueur` : ''}
            </span>
            {c.statut === 'retiree' ? (
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic' }}>retiré le {fmtDate(c.dateRetrait)}</span>
            ) : peutEmettre ? (
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                {/* Réimpression : l'exemplaire garde son numéro et son lieu — on
                    remplace une feuille abîmée, on n'en crée pas une seconde. */}
                <a href={`/api/ged/ged/docs/${doc.documentId}/apercu.pdf?telecharger=1&copie=${c.documentId}`}
                  target="_blank" rel="noreferrer" className="ged-btn"
                  style={{ ...styleBouton('contour', 'sm'), textDecoration: 'none' }}>
                  <I n="doc" s={12} />Imprimer
                </a>
                {estRQ && <ConfirmButton
                  label="" icon="trash" variant="outline" titre={`Retirer l’exemplaire n° ${c.numero}`}
                  question={`Retirer l’exemplaire n° ${c.numero} ? Confirmez qu’il a bien été récupéré.`}
                  confirmer="Retirer"
                  onConfirm={() => smq(`ged/copies/${c.documentId}/retrait`, {}, `Exemplaire n° ${c.numero} retiré du registre.`)} />}
              </span>
            ) : null}
          </div>
        );
      })}

      {peutEmettreIci && doc.statut === 'envigueur' && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTop: '1px dashed var(--border-default)' }}>
          <input value={lieu} onChange={(e) => setLieu(e.target.value)}
            placeholder="Où sera-t-il affiché ? (ex. paillasse hématologie)"
            onKeyDown={(e) => { if (e.key === 'Enter' && lieu.trim()) void emettre(); }}
            style={{ ...inputStyle, flex: '1 1 180px', height: 34, fontSize: 12.5 }} />
          {/* L'emplacement du plan. Sans option « — » muette : « Emplacement non
              précisé » DIT que la case est vide, au lieu de le laisser deviner. */}
          <select value={emplacementCode} onChange={(e) => setEmplacementCode(e.target.value)}
            aria-label="Emplacement au plan du laboratoire"
            style={{ ...inputStyle, flex: '1 1 170px', width: 'auto', height: 34, fontSize: 12.5 }}>
            <option value="">Emplacement non précisé</option>
            {emplacements.map((x) => <option key={x.code} value={x.code}>{x.chemin}</option>)}
          </select>
          <Bouton variante="primaire" taille="sm" onClick={() => void emettre()} disabled={!lieu.trim() || estOccupe()}
            chargement={estOccupe('emettre')} libelleChargement="Enregistrement…">
            <I n="plus" s={12} />Émettre un exemplaire
          </Bouton>
        </div>
      )}
      {peutEmettre && !estLaApplicable && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          Ce sont les exemplaires émis pour la v{version}. Un exemplaire ne s’émet que depuis la
          version <strong>en vigueur</strong> (v{doc.versionCourante}) : affichez-la pour en émettre un.
        </div>
      )}
      {peutEmettreIci && doc.statut !== 'envigueur' && (
        <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
          Un exemplaire ne s’émet que depuis la version <strong>en vigueur</strong>.
        </div>
      )}
    </div>
  );
}

/* Étiquettes libres du document : regroupement transversal pour le filtrage
   (distinct des mots-clés de contenu). Ouvert à tout rôle documentaire. */
function TagsEditor({ doc }: { doc: GedDocument }) {
  const { session, smq } = useGed();
  /* ⚠️ PAS DE VERSION ICI, et c'est une décision (RQ, 11/10/2026). Les
     étiquettes avaient été versionnées avec les autres rubriques : le champ se
     fermait alors sur toute version autre que le chantier, et l'on ne pouvait
     plus reclasser le document applicable pendant une révision. Une étiquette
     sert à RETROUVER, pas à décrire : elle n'a pas de circuit. */
  const peut = session.roles.length > 0;
  const tags = doc.tags || [];
  const [draft, setDraft] = useState('');
  const commit = (next: string[]) => void smq(`ged/docs/${doc.documentId}/tags`, { tags: next });
  const add = () => {
    const v = draft.trim();
    if (!v || tags.includes(v)) { setDraft(''); return; }
    commit([...tags, v]);
    setDraft('');
  };
  if (!peut && tags.length === 0) return null;
  return (
    <div style={{ ...card, padding: '16px 18px' }}>
      <h3 style={{ margin: '0 0 10px', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Étiquettes</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: peut ? 10 : 0 }}>
        {tags.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>Aucune étiquette.</span>}
        {tags.map((t) => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, background: 'var(--cyan-50)', color: 'var(--cyan-700)', borderRadius: 'var(--radius-pill)', padding: '3px 6px 3px 10px' }}>
            {t}
            {peut && <button onClick={() => commit(tags.filter((x) => x !== t))} title="Retirer" style={{ display: 'inline-flex', border: 'none', background: 'transparent', color: 'var(--cyan-700)', cursor: 'pointer', padding: 0 }}><I n="x" s={12} sw={2.4} /></button>}
          </span>
        ))}
      </div>
      {peut && (
        <div style={{ display: 'flex', gap: 6 }}>
          <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
            placeholder="Ajouter une étiquette…" maxLength={40} style={{ ...inputStyle, height: 32, fontSize: 12.5, flex: 1 }} />
          <Bouton taille="sm" onClick={add} disabled={!draft.trim()}>Ajouter</Bouton>
        </div>
      )}
    </div>
  );
}

/* Gestion RQ des intervenants du circuit : rédacteurs affectés (la liste
   gouverne qui peut éditer, contenu comme fichier OnlyOffice), vérificateur et
   approbateur. Le serveur revalide les rôles ; tout est tracé au journal. */
function IntervenantsManager({ doc, affectes, version }: {
  // Le document entier : la séparation des tâches se juge sur les versions
  // (contributeurs réels), pas seulement sur les intervenants désignés.
  doc: GedDocument;
  affectes: string[];
  /** La version EN CHANTIER, celle dont on désigne les intervenants. Vide pour
   *  un document neuf, qui n'a encore qu'une version. */
  version?: string;
}) {
  const { settings, act, refresh } = useGed();
  const [open, setOpen] = useState(false);
  const [redacteurs, setRedacteurs] = useState<string[]>(affectes);
  const [verificateur, setVerificateur] = useState(doc.verificateur || '');
  const [approbateur, setApprobateur] = useState(doc.approbateur || '');
  const [busy, setBusy] = useState(false);
  // Le panneau remplace son propre bouton d'ouverture : sans cela, le focus
  // clavier restait sur un élément démonté et la navigation repartait du début
  // du document. Échap referme, comme partout ailleurs.
  useEscape(open && !busy, () => setOpen(false));
  // Comptes suspendus / archivés exclus des affectations À VENIR : ils restent
  // dans l'organigramme et dans l'historique, mais on ne confie plus rien à
  // quelqu'un qui est absent ou parti.
  const porteurs = (role: string) => (settings?.gedUsers || []).filter((u) => (u.roles || []).includes(role) && estAffectable(u));

  // Un intervenant déjà en place peut avoir perdu (ou n'avoir jamais eu) le
  // rôle requis au paramétrage. Il n'est alors représentable ni par une case
  // ni par une option : le garder dans l'état l'enverrait en douce au serveur,
  // qui le refuse — refus incompréhensible et incorrigible depuis l'écran.
  // On repart donc des seules valeurs valides, en signalant ce qui a sauté.
  const idsPorteurs = (role: string) => new Set(porteurs(role).map((u) => u.id));
  const [ecartes, setEcartes] = useState<string[]>([]);

  /* LA RÈGLE VIT AU CHOIX (RQ, 03/10/2026) : « si je peux le sélectionner, c'est
     qu'il a le droit ». Rôle, séparation des tâches et habilitation sont jugés par
     le moteur (`POST ged/docs/:id/eligibilites`) sur le choix EN COURS — cocher
     un rédacteur grise aussitôt son nom dans les listes vérificateur / approbateur.
     L'écran ne refait aucun calcul : la même fonction refuse à l'enregistrement. */
  type Elig = { id: string; ok: boolean; raison?: string; derogation?: boolean };
  const [elig, setElig] = useState<Record<'redacteur' | 'verificateur' | 'approbateur', Elig[]> | null>(null);
  const [eligErreur, setEligErreur] = useState(false);
  React.useEffect(() => {
    if (!open) return undefined;
    let vivant = true;
    void (async () => {
      try {
        const r = await fetchEcriture(`/api/ged/ged/docs/${doc.documentId}/eligibilites`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ redacteurs, verificateur, approbateur }),
        });
        const d = await r.json().catch(() => null);
        if (!vivant) return;
        if (r.ok && d?.eligibles) { setElig(d.eligibles); setEligErreur(false); } else setEligErreur(true);
      } catch { if (vivant) setEligErreur(true); }
    })();
    return () => { vivant = false; };
  }, [open, doc.documentId, redacteurs, verificateur, approbateur]);
  const verdict = (role: 'redacteur' | 'verificateur' | 'approbateur', id: string): Elig | undefined => elig?.[role]?.find((x) => x.id === id);
  /** Les choix déjà faits que le moteur refuserait : ils bloquent l'enregistrement, avec la raison. */
  const choixRefuses: string[] = elig ? [
    ...redacteurs.filter((k) => verdict('redacteur', k) && !verdict('redacteur', k)!.ok).map((k) => `${userName(settings, k)} (rédacteur) : ${verdict('redacteur', k)!.raison}`),
    ...(verificateur && verdict('verificateur', verificateur) && !verdict('verificateur', verificateur)!.ok ? [`${userName(settings, verificateur)} (vérificateur) : ${verdict('verificateur', verificateur)!.raison}`] : []),
    ...(approbateur && verdict('approbateur', approbateur) && !verdict('approbateur', approbateur)!.ok ? [`${userName(settings, approbateur)} (approbateur) : ${verdict('approbateur', approbateur)!.raison}`] : []),
  ] : [];

  const ouvrir = () => {
    const okRed = idsPorteurs('redacteur');
    const okVer = idsPorteurs('verificateur');
    const okApp = idsPorteurs('approbateur');
    const perdus: string[] = [];
    const red = affectes.filter((k) => {
      if (okRed.has(k)) return true;
      perdus.push(`${userName(settings, k)} (rédacteur)`);
      return false;
    });
    const ver = doc.verificateur && okVer.has(doc.verificateur) ? doc.verificateur : '';
    if (doc.verificateur && !ver) perdus.push(`${userName(settings, doc.verificateur)} (vérificateur)`);
    const app = doc.approbateur && okApp.has(doc.approbateur) ? doc.approbateur : '';
    if (doc.approbateur && !app) perdus.push(`${userName(settings, doc.approbateur)} (approbateur)`);

    setRedacteurs(red);
    setVerificateur(ver);
    setApprobateur(app);
    setEcartes(perdus);
    setOpen(true);
  };
  const save = async () => {
    setBusy(true);
    const ok = await act(doc.documentId, 'redacteurs', { redacteurs, verificateur, approbateur });
    setBusy(false);
    if (ok) { setOpen(false); return; }
    // Refus serveur : le plus souvent la fiche a avancé dans le circuit depuis
    // l'ouverture du panneau (elle n'est plus « en rédaction »), mais notre
    // copie était périmée. `act` ne resynchronise pas sur échec — on le fait
    // ici. Pas de fermeture manuelle : si la fiche a quitté la rédaction, la
    // carte démonte ce panneau (donc plus de refus rejouable) ; si c'était une
    // erreur passagère, elle reste en rédaction et la saisie n'est pas perdue.
    await refresh();
  };

  if (!open) {
    return (
      <Bouton taille="sm" onClick={ouvrir} style={{ marginTop: 10, width: '100%' }}>
        <I n="pen" s={12} />Gérer les intervenants{version ? ` de la v${version}` : ''}
      </Bouton>
    );
  }
  const selectStyle = { ...inputStyle, padding: '6px 8px', fontSize: 12.5 };
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)', display: 'grid', gap: 10 }}>
      {ecartes.length > 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.45, background: 'var(--amber-50)', border: '1px solid var(--amber-600)', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}>
          Retiré{ecartes.length > 1 ? 's' : ''} faute du rôle requis au paramétrage : {ecartes.join(', ')}.
          Redésignez quelqu’un ci-dessous, ou accordez-lui le rôle dans Paramétrage.
        </div>
      )}
      <div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6 }}>Rédacteurs affectés (coédition)</div>
        <div style={{ display: 'grid', gap: 4, maxHeight: 132, overflowY: 'auto' }}>
          {porteurs('redacteur').map((u, i) => {
            const v = verdict('redacteur', u.id);
            const bloque = !!v && !v.ok && !redacteurs.includes(u.id); // on peut toujours DÉCOCHER
            return (
              <label key={u.id} title={bloque ? v?.raison : undefined}
                style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, cursor: bloque ? 'not-allowed' : 'pointer', opacity: bloque ? 0.55 : 1 }}>
                <input type="checkbox" autoFocus={i === 0} checked={redacteurs.includes(u.id)} disabled={bloque}
                  onChange={(e) => setRedacteurs(e.target.checked ? [...redacteurs, u.id] : redacteurs.filter((k) => k !== u.id))} />
                {u.nom}{bloque ? ` — ${v?.raison}` : ''}
              </label>
            );
          })}
        </div>
        {!redacteurs.length && <div style={{ fontSize: 12.5, color: 'var(--amber-600)', marginTop: 4 }}>Au moins un rédacteur est requis.</div>}
      </div>
      {(['verificateur', 'approbateur'] as const).map((role) => {
        const valeur = role === 'verificateur' ? verificateur : approbateur;
        const changer = role === 'verificateur' ? setVerificateur : setApprobateur;
        return (
          <label key={role} style={{ display: 'block' }}>
            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>
              {role === 'verificateur' ? 'Vérificateur' : 'Approbateur'}
            </span>
            <Selecteur value={valeur} onChange={changer} style={selectStyle}>
              <option value="">— non désigné —</option>
              {porteurs(role).concat(role === 'approbateur' ? (settings?.gedUsers || []).filter((u) => (u.roles || []).includes('rq') && !(u.roles || []).includes('approbateur') && estAffectable(u)) : []).map((u) => {
                const v = verdict(role, u.id);
                const refuse = !!v && !v.ok;
                return (
                  <option key={u.id} value={u.id} disabled={refuse && u.id !== valeur} title={v?.raison}>
                    {u.nom}{refuse ? ` — ${v?.raison}` : v?.derogation ? ' — dérogation du RQ (motif à la signature)' : ''}
                  </option>
                );
              })}
            </Selecteur>
          </label>
        );
      })}
      {eligErreur && (
        <div style={{ fontSize: 12.5, color: 'var(--amber-600)', lineHeight: 1.45 }}>
          Les règles de désignation n’ont pas pu être lues : le moteur les appliquera à l’enregistrement.
        </div>
      )}
      {choixRefuses.length > 0 && (
        <div role="alert" style={{ fontSize: 12.5, color: 'var(--red-600)', lineHeight: 1.45, border: '1px solid currentColor', borderRadius: 'var(--radius-sm)', padding: '8px 10px' }}>
          Désignation impossible en l’état :
          <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>{choixRefuses.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      <PiedActions
        taille="sm"
        annuler={{ onClick: () => setOpen(false) }}
        principale={{ libelle: 'Enregistrer', onClick: () => void save(), disabled: busy || !redacteurs.length || choixRefuses.length > 0 }}
      />
    </div>
  );
}

/* ------------------------------- Fiche ------------------------------- */
/**
 * Les deux actes du document externe.
 *
 * Le bloc ne s'affiche que sur un document de type « DE ». Ailleurs il serait
 * non seulement inutile mais trompeur : proposer « adopter » sur une procédure
 * que le laboratoire a rédigée laisserait croire qu'on peut contourner la
 * vérification et l'approbation.
 */
/**
 * Appel des deux actes du parcours externe.
 *
 * Sorti du composant parce que les boutons vivent désormais dans la BARRE
 * D'ACTIONS de la fiche — là où la RQ valide d'habitude une rédaction — et le
 * panneau de motif plus bas. Deux endroits, un seul appel : dupliquer le fetch
 * aurait suffi à ce que l'un des deux oublie le rafraîchissement et laisse
 * l'écran afficher l'ancien statut après une adoption réussie.
 */
async function appelerParcoursExterne(
  documentId: string,
  acte: 'adopter' | 'remplacer',
  corps: Record<string, unknown>,
  setErreur: (m: string) => void,
  refresh: () => Promise<void> | void,
): Promise<boolean> {
  setErreur('');
  const r = await fetchEcriture(`/api/ged/ged/docs/${documentId}/externe/${acte}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    setErreur(j?.error?.message || 'Action refusée.');
    return false;
  }
  await refresh();
  return true;
}

function ParcoursExterne({ doc }: { doc: GedDocument }) {
  const { session } = useGed();
  if (String(doc.docType || '').toUpperCase() !== 'DE') return null;
  const rq = !!session?.roles?.includes('rq');

  return (
    <div style={{ ...card, padding: '14px 18px', display: 'grid', gap: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Document externe</div>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
        Ce texte n’a pas été rédigé par le laboratoire : il ne se vérifie ni ne s’approuve.
        Il s’<strong>enregistre</strong>, puis le laboratoire l’<strong>adopte</strong> — c’est
        l’édition contre laquelle il s’audite — jusqu’à ce qu’une édition postérieure la remplace.
        {rq
          ? ' Les deux actes sont dans la barre d’actions, en haut de la fiche.'
          : ' L’adoption est prononcée par le responsable qualité.'}
      </p>
    </div>
  );
}

export function DocDetail() {
  // L'écran lit le document SÉLECTIONNÉ ; le panneau, lui, reçoit un code (voir
  // `DocDossier` juste dessous). Un seul rendu derrière les deux chemins.
  const doc = useSelectedDoc();
  return <Fiche doc={doc} />;
}

/**
 * Le DOSSIER d'un document — exporté sous le nom `DocDossier`, sur le modèle
 * d'`ActionDossier` (Actions.tsx).
 *
 * Il est désigné par son CODE, et non par son `documentId` : le code est la clé
 * de sélection du magasin (`openDoc(code)`), c'est lui que portent les liens et
 * le journal, et c'est donc lui que le panneau a sous la main.
 *
 * `enPanneau` ne touche pas au contenu de la fiche : il retire seulement ce qui
 * ferait QUITTER l'écran de dessous — le retour au catalogue, le passage au
 * circuit de revue — et la zone d'impression, qui ne peut pas exister en double.
 */
export function DocDossier({ code, enPanneau = false, onglet, voirEnCours }: {
  code: string;
  enPanneau?: boolean;
  /** Onglet d'arrivée (une tâche « à rédiger » vise le fichier). */
  onglet?: 'lecture' | 'fichier' | 'versions' | 'journal' | 'diffusion' | 'comments' | 'liens';
  /** Arriver sur la version EN CHANTIER — l'exception du 06/10/2026. */
  voirEnCours?: boolean;
}) {
  const { docs, booted, loading } = useGed();
  const doc = docs.find((d) => d.code === code) || null;
  // Catalogue pas encore arrivé : « n'est plus au catalogue » serait un faux
  // vide (voir `enChargement`).
  if (!doc && enChargement(booted, loading, docs.length)) return <Skeleton rows={3} />;
  // Le panneau survit aux rafraîchissements de la liste : un document sorti du
  // catalogue entre-temps doit se dire, pas se rendre en fiche vide.
  if (!doc) return <EmptyState titre="Ce document n’est plus au catalogue." indice="Il a pu être retiré depuis l’ouverture de cet écran." />;
  return <Fiche doc={doc} enPanneau={enPanneau} ongletInitial={onglet} voirEnCoursInitial={!!voirEnCours} />;
}

/** Tant que le document n'est pas DIFFUSÉ, ses métadonnées et ses intervenants se
 *  modifient (RQ, 03/10/2026) — y compris en vérification et en approbation. Même
 *  liste que le serveur (`STATUTS_AVANT_DIFFUSION`, ged.ts). */
const STATUTS_AVANT_DIFFUSION: readonly string[] = ['brouillon', 'enrevision', 'averifier', 'aapprouver'];

/**
 * Correction des MÉTADONNÉES d'un document — RQ, motivée, journalisée.
 *
 * La route serveur (`POST ged/docs/:id/metadonnees`) existait, réservée au RQ
 * et inscrite au journal signé avec l'avant/après champ par champ. Mais aucun
 * écran ne l'appelait : le RQ constatait une erreur de titre, de processus ou
 * de périmètre sans aucun moyen de la corriger lui-même — il fallait passer par
 * l'API. C'est ce que la RQ a résumé le 14/08/2026 : « on dirait que c'est
 * uniquement géré par toi ».
 *
 * ── Ce qui reste hors d'atteinte, et pourquoi ───────────────────────────────
 *
 * · le CODE : il identifie le document dans le journal chaîné (`docCode`), le
 *   changer orphelinerait tout son historique. Le serveur refuse, avec le motif ;
 * · le STATUT, les versions, les dates de vigueur : ils se DÉCIDENT par le
 *   circuit et se prouvent par des signatures. Les corriger comme une rubrique
 *   descriptive reviendrait à antidater une mise en vigueur.
 *
 * La correction n'est offerte que tant que le document n'est pas diffusé
 * (rédaction, vérification, approbation) — au-delà, ce que les lecteurs ont sous les yeux
 * ne se retouche pas en silence.
 */
/**
 * Correction de l'INTITULÉ d'un document EN VIGUEUR — RQ, motivée, journalisée.
 *
 * L'intitulé n'est pas une rubrique opposable : il se corrige « tout de suite,
 * partout » (décision RQ du 11/10/2026, `fiche-version.ts`), et l'archive de la
 * version garde le titre sous lequel elle a été signée. Il n'y avait pourtant
 * aucun geste pour le faire une fois le document diffusé : la correction des
 * métadonnées s'arrête à la diffusion, et c'est voulu pour le reste (type,
 * périmètre, périodicité ne changent qu'avec une révision approuvée).
 *
 * On n'envoie donc QUE `titre` et `motif` : la route ne touche à rien d'autre,
 * et rien d'opposable ne bouge sans circuit.
 */
function CorrigerIntitule({ doc, onFerme }: { doc: GedDocument; onFerme: () => void }) {
  const { smq } = useGed();
  const { run, estOccupe } = useBusy();
  const [titre, setTitre] = useState(doc.titre || '');
  const [motif, setMotif] = useState('');
  const declaration = React.useMemo(() => [
    { id: 'intitule-titre', label: 'Intitulé', valeur: titre, requis: true },
    { id: 'intitule-motif', label: 'Motif de la correction', valeur: motif, requis: true },
  ], [titre, motif]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);
  const inchange = titre.trim() === String(doc.titre || '').trim();

  return (
    <Modale
      titre={`Corriger l’intitulé — ${doc.code}`}
      sousTitre="Le nouvel intitulé s’applique immédiatement, partout. Les versions archivées gardent celui sous lequel elles ont été signées. La correction part au journal signé, avec l’ancien intitulé en regard."
      onClose={onFerme}
      largeur={620}
      pied={(
        <PiedActions
          style={{ width: '100%' }}
          annuler={{ onClick: onFerme }}
          principale={{
            libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('intitule'), disabled: estOccupe() || inchange,
            onClick: () => void run('intitule', async () => {
              if (!verifier()) return;
              const ok = await smq(`ged/docs/${doc.documentId}/metadonnees`,
                { titre: titre.trim(), motif: motif.trim() },
                'Intitulé corrigé — modification tracée au journal signé.');
              if (ok) onFerme();
            }),
          }}
        />
      )}>
      <div ref={refFormulaire} style={{ display: 'grid', gap: 12 }}>
        <Bandeau />
        <div>
          <label style={lblMeta} htmlFor="intitule-titre">Intitulé <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <input id="intitule-titre" value={titre} onChange={(e) => setTitre(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
        </div>
        <div>
          <label style={lblMeta} htmlFor="intitule-motif">Motif de la correction <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <textarea id="intitule-motif" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)}
            placeholder="Coquille, intitulé imprécis, alignement sur le manuel…"
            style={{ ...inputStyle, width: '100%', height: 'auto', resize: 'vertical' }} />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Type, périmètre, périodicité et autres rubriques opposables ne changent qu’avec une révision approuvée.
          </span>
        </div>
      </div>
    </Modale>
  );
}

function CorrigerMetadonnees({ doc, onFerme }: { doc: GedDocument; onFerme: () => void }) {
  const { settings, smq } = useGed();
  const { run, estOccupe } = useBusy();
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  /* ── ON CORRIGE LA FICHE DE LA VERSION VISÉE (RQ, 10/10/2026) ─────────────
     Pendant une révision, le serveur écrit la correction sur la version EN
     CHANTIER (`cibleFiche`). Préremplir le formulaire avec les rubriques du
     DOCUMENT aurait alors deux effets, l'un absurde et l'autre dangereux :
     une correction déjà faite dans le chantier se présenterait comme à refaire,
     et le simple fait d'enregistrer la rubrique affichée la REMETTRAIT à la
     valeur applicable — en défaisant le travail, sans rien dire. */
  const cible = cibleFiche(doc);
  const versionCible = cible === 'candidate' ? versionCandidate(doc.versions) : versionApplicable(doc.versions);
  const base = cible === 'candidate' ? ficheDeVersion(doc, versionCible) : ficheCourante(doc);
  /* ⚠️ L'INTITULÉ VIENT DU DOCUMENT, toujours (RQ, 11/10/2026) : il n'est pas
     versionné à la saisie, et le lire dans la fiche du chantier proposerait de
     « corriger » une valeur figée au déclenchement de la révision — puis
     l'écrirait sur le document, écrasant la correction faite depuis. */
  const txt = (c: keyof Fiche) => String((c === 'titre' ? ficheCourante(doc)[c] : base[c]) ?? '');
  const lst = (c: keyof Fiche) => (Array.isArray(base[c]) ? (base[c] as unknown[]).map(String) : []);
  const [f, setF] = useState({
    titre: txt('titre'),
    docType: txt('docType'),
    processus: txt('processus'),
    discipline: txt('discipline'),
    /* PÉRIMÈTRE = LE SITE où le document s'applique (RQ, 04/10/2026), choisi
       dans une liste fermée. « Qui doit le lire » n'est plus ici : c'est
       l'AUDIENCE, choisie à la diffusion. Une valeur ancienne qui n'est pas un
       site (une phrase « Fonctions : … ») est remplacée par le site du
       document — visible avant d'enregistrer, et journalisée si l'on enregistre. */
    perimetre: (settings?.sites || []).includes(txt('perimetre'))
      ? txt('perimetre')
      : ((settings?.sites || []).includes(txt('site')) ? txt('site') : (settings?.activeSite || (settings?.sites || [])[0] || '')),
    periodicite: txt('periodicite'),
  });
  const [restreint, setRestreint] = useState(doc.telechargementRestreint === true);
  /* LES MOTS-CLÉS, repris tels quels puis modifiables (RQ, 24/08/2026 :
     « comment sont générés les mots clés ? et je voudrais avoir la main après
     génération »).

     Ils sont PROPOSÉS à la création — par l'assistance quand elle a lu le
     fichier, ou par la demande qui a produit le document — et le serveur les
     acceptait déjà en correction (`CHAMPS` de `POST ged/docs/:id/metadonnees`).
     Seul l'écran ne les offrait pas : ils s'affichaient sur la fiche, et rien
     ne permettait d'en retirer un. Une proposition qu'on ne peut pas corriger
     n'est pas une proposition, c'est une décision prise à votre place. */
  const [motsCles, setMotsCles] = useState<string[]>(lst('motsCles'));
  const [motNouveau, setMotNouveau] = useState('');
  const ajouterMot = () => {
    const m = motNouveau.trim().toLowerCase();
    /* Vides et doublons écartés SANS message : ce sont des gestes de saisie,
       pas des fautes — refuser bruyamment un doublon ferait chercher une
       erreur là où il n'y en a pas. */
    if (!m || motsCles.some((x) => x.toLowerCase() === m)) { setMotNouveau(''); return; }
    setMotsCles([...motsCles, motNouveau.trim()]);
    setMotNouveau('');
  };
  const maj = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));

  const declaration = React.useMemo(() => [
    { id: 'meta-titre', label: 'Intitulé', valeur: f.titre, requis: true },
    { id: 'meta-type', label: 'Type', valeur: f.docType, requis: true },
    { id: 'meta-proc', label: 'Processus', valeur: f.processus },
    { id: 'meta-disc', label: 'Discipline', valeur: f.discipline },
    /* Le périmètre n'est PAS déclaré obligatoire : un document peut légitimement
       n'en porter aucun. Il sort donc du bandeau des champs manquants, où il
       n'aurait plus d'ancre depuis qu'il n'est plus un champ de saisie. */
    { id: 'meta-motif', label: 'Motif de la correction', valeur: motif, requis: true },
  ], [f, motif]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);

  const procs = (settings?.processus || []).filter((p) => p.active !== false);
  const discs = (settings?.disciplines || []).filter((d) => d.active !== false);

  return (
    <Modale
      titre={cible === 'candidate'
        ? `Modifier les métadonnées de la v${String(versionCible?.num || '')} — ${doc.code}`
        : `Modifier les métadonnées — ${doc.code}`}
      sousTitre={cible === 'candidate'
        ? `Ces rubriques appartiennent à la version EN COURS DE RÉVISION : elles remplaceront celles de la v${String(versionApplicable(doc.versions)?.num || doc.versionCourante)}, qui s’applique aujourd’hui, à l’APPROBATION — pas avant. L’INTITULÉ fait exception : il s’applique immédiatement, partout. Chaque modification part au journal signé, avec son ancienne valeur en regard.`
        : 'Chaque rubrique modifiée part au journal SIGNÉ, avec son ancienne valeur en regard.'}
      onClose={onFerme}
      largeur={720}
      pied={(
        <PiedActions
          style={{ width: '100%' }}
          annuler={{ onClick: onFerme }}
          principale={{
            libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('meta'), disabled: estOccupe(),
            onClick: () => void run('meta', async () => {
              if (!verifier()) return;
              setErreur('');
              const ok = await smq(`ged/docs/${doc.documentId}/metadonnees`,
                {
                  ...f,
                  telechargementRestreint: restreint,
                  /* Le mot en cours de SAISIE part avec les autres : sans cela,
                     on tape un mot-clé, on clique « Enregistrer », et il
                     disparaît sans un mot — le geste le plus naturel serait
                     aussi celui qui perd le travail. */
                  motsCles: motNouveau.trim() && !motsCles.some((x) => x.toLowerCase() === motNouveau.trim().toLowerCase())
                    ? [...motsCles, motNouveau.trim()]
                    : motsCles,
                  motif: motif.trim(),
                },
                'Métadonnées corrigées — modification tracée au journal signé.');
              if (ok) onFerme();
            }),
          }}
        />
      )}>
      <div ref={refFormulaire} style={{ display: 'grid', gap: 12 }}>
        <Bandeau />
        <div>
          <label style={lblMeta} htmlFor="meta-titre">Intitulé <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <input id="meta-titre" value={f.titre} onChange={(e) => maj('titre', e.target.value)} style={{ ...inputStyle, width: '100%' }} />
        </div>
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px' }}>
          <div>
            <label style={lblMeta} htmlFor="meta-type">Type <span style={{ color: 'var(--red-600)' }}>*</span></label>
            <select id="meta-type" value={f.docType} onChange={(e) => maj('docType', e.target.value)} style={inputStyle}>
              {(settings?.docTypes || []).filter((t) => t.active).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={lblMeta} htmlFor="meta-per">Périodicité de revue</label>
            <select id="meta-per" value={f.periodicite} onChange={(e) => maj('periodicite', e.target.value)} style={inputStyle}>
              {PERIODICITES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          {/* Les DEUX axes, séparés — c'est le sens de la reprise du 14/08. */}
          <div>
            <label style={lblMeta} htmlFor="meta-proc">Processus</label>
            <select id="meta-proc" value={f.processus} onChange={(e) => maj('processus', e.target.value)} style={inputStyle}>
              <option value="">— aucun</option>
              {procs.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label style={lblMeta} htmlFor="meta-disc">Discipline (famille biologique)</label>
            <select id="meta-disc" value={f.discipline} onChange={(e) => maj('discipline', e.target.value)} style={inputStyle}>
              <option value="">— aucune</option>
              {discs.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={lblMeta} htmlFor="meta-perim">Périmètre — site où le document s’applique</label>
          <select id="meta-perim" value={f.perimetre} onChange={(e) => maj('perimetre', e.target.value)} style={inputStyle}>
            {(settings?.sites || []).map((site) => <option key={site} value={site}>{site}</option>)}
          </select>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '5px 0 0', lineHeight: 1.45 }}>
            Qui doit le lire se choisit à la diffusion, dans l’onglet Diffusion.
          </p>
        </div>

        {/* ── MOTS-CLÉS ────────────────────────────────────────────────────
            Ils servent à RETROUVER le document par la recherche générale, pas
            à le classer : le classement, ce sont le type, le processus et la
            discipline, qui ont chacun leur liste fermée. */}
        <div>
          <label style={lblMeta} htmlFor="meta-mot">Mots-clés</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 7 }}>
            {motsCles.length === 0 && (
              <span style={{ fontSize: 12.5, color: 'var(--text-faint)', fontStyle: 'italic' }}>
                aucun — le document se retrouve alors par son intitulé et sa référence
              </span>
            )}
            {motsCles.map((m) => (
              <span key={m} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
                background: 'var(--surface-sunken)', border: '1px solid var(--border-default)',
                borderRadius: 'var(--radius-pill)', padding: '3px 5px 3px 10px', color: 'var(--text-body)',
              }}>
                {m}
                <button type="button" className="ged-btn" aria-label={`Retirer le mot-clé ${m}`}
                  onClick={() => setMotsCles(motsCles.filter((x) => x !== m))}
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0 3px', lineHeight: 1 }}>×</button>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 7 }}>
            <input id="meta-mot" value={motNouveau} onChange={(e) => setMotNouveau(e.target.value)}
              /* Entrée ajoute : sans cela on tape un mot, on clique « Enregistrer »,
                 et le mot en cours de saisie est perdu sans un mot. */
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouterMot(); } }}
              placeholder="Ajouter un mot-clé, puis Entrée" style={{ ...inputStyle, flex: 1 }} />
            <Bouton taille="sm" type="button" disabled={!motNouveau.trim()} onClick={ajouterMot}>Ajouter</Bouton>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
            Proposés à la création — par l’assistance quand elle a lu le fichier, ou par la demande
            qui l’a produit. Vous les corrigez ici ; chaque modification part au journal signé.
          </div>
        </div>
        {/* Exemplaire sous licence — la décision appartient au RQ, document par
            document. Offerte aux SEULS documents externes : sur un document que
            le laboratoire rédige, restreindre le téléchargement de sa propre
            production n'aurait aucun sens. */}
        {doc.classe === 'importe' && (
          <div style={{ background: 'var(--gray-25)', borderRadius: 'var(--radius-md)', padding: '10px 12px' }}>
            <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input id="meta-licence" type="checkbox" checked={restreint}
                onChange={(e) => setRestreint(e.target.checked)}
                style={{ marginTop: 2, accentColor: 'var(--color-primary)' }} />
              <span style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
                <strong style={{ color: 'var(--text-strong)' }}>Exemplaire sous licence — consultable, non téléchargeable.</strong>
                {' '}Le personnel pourra le <strong>lire en entier</strong> dans l’onglet Aperçu, mais ni le
                télécharger, ni l’imprimer depuis l’éditeur. Vous, responsable qualité, gardez l’accès au
                fichier — c’est vous qui détenez la licence et devez pouvoir le produire en audit.
                <span style={{ display: 'block', marginTop: 4, color: 'var(--text-muted)', fontSize: 12.5 }}>
                  Ce n’est pas une protection technique : une capture d’écran reste possible. C’est la trace
                  d’une décision — héberger un référentiel acheté est permis, en redistribuer des copies ne l’est pas.
                </span>
              </span>
            </label>
          </div>
        )}

        <div>
          <label style={lblMeta} htmlFor="meta-motif">Motif de la correction <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <textarea id="meta-motif" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)}
            placeholder="Ce qui justifie la correction — erreur de saisie, reclassement, périmètre mal décrit…"
            style={{ ...inputStyle, width: '100%', height: 'auto', resize: 'vertical' }} />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Le code <strong style={{ fontFamily: 'var(--font-mono)' }}>{doc.code}</strong> n’est pas modifiable :
            il identifie le document dans le journal chaîné.
          </span>
        </div>
        <FieldError>{erreur}</FieldError>
      </div>
    </Modale>
  );
}

const lblMeta: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 4 };

function Fiche({ doc, enPanneau = false, ongletInitial, voirEnCoursInitial = false }: {
  doc: GedDocument | null;
  enPanneau?: boolean;
  /** Onglet d'arrivée imposé par l'appelant (tâche « à rédiger »). */
  ongletInitial?: 'lecture' | 'fichier' | 'versions' | 'journal' | 'diffusion' | 'comments' | 'liens';
  /** Arriver directement sur la version EN CHANTIER (tâches de circuit). */
  voirEnCoursInitial?: boolean;
}) {
  // Les versions du catalogue pointent vers sa route livret : adresse du
  // catalogue résolue à l'exécution (posée par le layout).
  const { manuel: MANUEL_URL } = useUrlsModules();
  const { go, panneauEnPleinePage, session, settings, docTab: docTabEcran, setDocTab: setDocTabEcran, docVoirEnCours: voirEnCoursEcran, setDocVoirEnCours: setVoirEnCoursEcran, act, loadJournal, events, liens, refresh, docs, booted, loading, selectedCode } = useGed();
  /* Un document EXTERNE ne suit pas le circuit de rédaction : il n'a ni
     rédacteur, ni vérificateur, ni approbateur, et son parcours propre est
     enregistré → adopté → remplacé. `docType` est ce qui le dit — `classe`
     parle du FORMAT du fichier (un mode opératoire rédigé ici puis déposé en
     Word est « importé » sans être externe pour autant). */
  const externe = String(doc?.docType || '').trim().toUpperCase() === 'DE';
  // En panneau, l'onglet est LOCAL. `docTab` du magasin appartient à l'écran qui
  // se trouve dessous : cliquer « Journal » dans le panneau lui aurait changé son
  // onglet sous les pieds, et le panneau suivant aurait hérité de celui-là.
  const [docTabPanneau, setDocTabPanneau] = useState<typeof docTabEcran>(ongletInitial || 'lecture');
  const docTabDemande = enPanneau ? docTabPanneau : docTabEcran;
  /* « VOIR LA VERSION EN CHANTIER » — même partage que l'onglet : LOCAL en
     panneau, dans le magasin pour l'écran. Posé par les tâches « à rédiger /
     à vérifier / à approuver » (l'exception demandée le 06/10/2026), et par le
     lien de la mention sur la fiche. */
  const [voirEnCoursPanneau, setVoirEnCoursPanneau] = useState(voirEnCoursInitial);
  const voirEnCoursDemande = enPanneau ? voirEnCoursPanneau : voirEnCoursEcran;
  const setVoirEnCours = enPanneau ? setVoirEnCoursPanneau : setVoirEnCoursEcran;
  /* ── UNE VERSION CHOISIE À LA MAIN (onglet Versions, 06/10/2026) ──────────
     Le drapeau `voirEnCours` ne distingue que deux versions, l'applicable et le
     chantier. Depuis que chaque version a un exemplaire archivé, on peut
     demander N'IMPORTE LAQUELLE — y compris une obsolète.
     ⚠️ Le CODE est gardé avec le numéro : la fiche de l'écran ne se démonte pas
     quand on passe d'un document à l'autre, et un numéro resté en mémoire
     s'appliquerait au document suivant, qui ne l'a pas. */
  const [choixVersion, setChoixVersion] = useState<{ code: string; num: string } | null>(null);
  const versionChoisie = choixVersion && doc && choixVersion.code === doc.code ? choixVersion.num : null;
  const choisirVersion = (num: string | null) => setChoixVersion(num && doc ? { code: doc.code, num } : null);
  /* Les versions dont un exemplaire est ARCHIVÉ — lues sur le disque par le
     moteur, donc absentes de la lecture REST du document. `null` = on ne sait
     pas encore : on ne propose rien ET on n'affirme rien (faux vides). */
  const [archivees, setArchivees] = useState<string[] | null>(null);
  const [changementsOuverts, setChangementsOuverts] = useState<string | null>(null);

  /* ── DEUX VERSIONS VIVANTES, ET UNE SEULE QUI S'APPLIQUE ──────────────────
     Pendant une révision, le document a une version EN VIGUEUR (celle que le
     laboratoire applique ce matin) et une version EN CHANTIER. La fiche montre
     la PREMIÈRE par défaut, avec une mention cliquable vers la seconde — RQ du
     06/10/2026. Jusque-là c'était l'inverse : l'aperçu était masqué au motif
     qu'une révision était ouverte, et l'onglet Fichier affichait le brouillon.
     ⚠️ `revision` ne se lit PAS dans le statut : une révision soumise porte
     `averifier` ou `aapprouver` alors que l'ancienne version s'applique
     toujours. Voir `lib/version-en-cours.ts`. */
  const versionAppl = versionApplicable(doc?.versions);
  const versionCand = versionCandidate(doc?.versions);
  const revision = revisionEnCours(doc);
  const accesEnCours = accesVersionEnCours(doc, session);
  /* La source d'un document EN VIGUEUR (RQ, 20/10/2026) : onglet Fichier au RQ
     seul — même le rédacteur, sa mission finie ; les autres lisent l'Aperçu. */
  const voitSource = accesSourceEnVigueur(doc, session).voir;
  const etapeCand = etapeEnCours(doc?.statut);
  // Le serveur refuse de toute façon : l'écran ne montre pas ce qu'il refusera.
  const voirEnCours = revision ? voirEnCoursDemande && accesEnCours.ouvert : voirEnCoursDemande;
  /* LA version que la fiche montre, et il n'y en a qu'une : le choix explicite
     s'il y en a un, sinon le chantier quand on vient le traiter, sinon ce qui
     s'applique. Chaîne vide = aucune version identifiable (document sans
     version vivante) : l'aperçu reprend alors son défaut. */
  const numApplicable = String(versionAppl?.num || '');
  const numCandidat = String(versionCand?.num || '');
  const versionVue = versionChoisie
    || (revision && voirEnCours ? numCandidat : numApplicable || numCandidat)
    || '';
  // Une version choisie qui n'est ni l'applicable ni le chantier : une archive.
  const litUneArchive = !!versionChoisie && versionChoisie !== numApplicable && versionChoisie !== numCandidat;
  /* ── LA FICHE SUIT LA VERSION AFFICHÉE (RQ, 10/10/2026) ───────────────────
     « Dans la v2, les métadonnées, les copies en service, les étiquettes…
     peuvent être différentes de la v1. Actuellement je les visualise
     identiques. » Elles l'étaient parce que le panneau lisait les colonnes du
     DOCUMENT, qui n'ont qu'une valeur — celle d'aujourd'hui. Chaque version
     porte désormais sa fiche (`lib/fiche-version.ts`), et c'est celle de la
     version lue qui s'affiche. Les versions antérieures au 10/10/2026 n'en ont
     pas : on montre alors celle du document EN LE DISANT (`ficheArchivee`). */
  const versionObjVue = (doc?.versions || []).find((v) => String(v.num) === versionVue) || null;
  /* `ficheAffichee` et non `ficheDeVersion` : les rubriques versionnées
     viennent de la version lue, l'INTITULÉ du document — sauf sur une version
     révolue, qui garde le titre sous lequel elle a été signée (décision RQ du
     11/10/2026, voir `lib/fiche-version.ts`). */
  const ficheVue = ficheAffichee(doc, versionObjVue);
  // Ce que la version lue dit AUTREMENT que le document (donc autrement que ce
  // qui s'applique) : c'est la seule chose que l'œil doit pouvoir repérer.
  const ecartsVue = ecartsFiche(ficheCourante(doc), ficheVue);
  const litLeChantier = !!numCandidat && versionVue === numCandidat && !!numApplicable;
  /* Les intervenants AFFICHÉS sont ceux de la version lue : une version
     approuvée a figé les siens (`versions[].verificateur` / `approbateur`), et
     les relire dans les colonnes du document ferait changer la signature d'une
     version APPROUVÉE au gré des désignations de la révision suivante — la
     demande de la RQ du 10/10 : « une fois une version approuvée, plus aucun
     champ du circuit ne peut être modifié ». */
  const versionSignee = !!versionObjVue?.dateVigueur;
  const circuitVue = {
    redacteurs: versionSignee
      ? Array.from(new Set([...(versionObjVue?.contributeurs || []), versionObjVue?.redacteur].filter(Boolean) as string[]))
      : null,
    verificateur: versionSignee ? (versionObjVue?.verificateur || '') : null,
    approbateur: versionSignee ? (versionObjVue?.approbateur || '') : null,
  };
  // L'onglet Fichier n'existe plus en circuit (voir `fichierVisible`) : un onglet
  // mémorisé sur « fichier » retombe sur l'aperçu au lieu d'un écran vide.
  const docTab = docTabDemande === 'fichier' && doc && !externe
    && (['averifier', 'aapprouver'].includes(doc.statut)
      // Révision dont le travail en cours ne m'est pas ouvert : l'onglet Fichier
      // n'existe pas pour moi (il ne contient que le brouillon), on retombe sur
      // l'aperçu — c'est-à-dire sur la version applicable.
      || (revision && !accesEnCours.ouvert)
      || !voitSource) ? 'lecture' : docTabDemande;
  const setDocTab = enPanneau ? setDocTabPanneau : setDocTabEcran;
  // Le jour est figé au montage : lire l'heure pendant le rendu est impur
  // (React 19 le signale), et comparer une échéance à minuit avec l'instant
  // présent ferait basculer « en retard » dès 00 h 01 le jour même. Une lecture
  // due AUJOURD'HUI n'est pas en retard.
  const [aujourdhui] = useState(() => aujourdhuiIso());
  const { demander, confirmation } = useConfirmation();
  const [commentDraft, setCommentDraft] = useState('');
  // Le motif de révision passait par `window.prompt` : panneau en place, comme
  // partout ailleurs dans le module.
  const [revisionMotif, setRevisionMotif] = useState<string | null>(null);
  /* Parcours du document externe. `null` = panneau fermé ; une chaîne = ouvert
     avec ce qui a été tapé. Même patron que le motif de révision juste
     au-dessus, pour que les deux gestes se ressemblent à l'usage. */
  const [remplacementMotif, setRemplacementMotif] = useState<string | null>(null);
  /* Abandon d'une fiche jamais diffusée. Même patron que les deux motifs
     au-dessus : `null` = fermé, une chaîne = ouvert. */
  const [abandonMotif, setAbandonMotif] = useState<string | null>(null);
  const [erreurExterne, setErreurExterne] = useState('');
  // Gel de l'applicabilité (GED-5) : même panneau en place que la révision —
  // `null` = fermé, chaîne = ouvert. Deux états distincts, car poser un gel et
  // le lever ne demandent pas la même chose (une échéance facultative pour l'un).
  const [gelMotif, setGelMotif] = useState<string | null>(null);
  /** Retrait d'urgence depuis la fiche — même convention : `null` = fermé. */
  const [urgenceMotif, setUrgenceMotif] = useState<string | null>(null);
  /** Correction des rubriques descriptives — RQ, motivée, journalisée. */
  const [corrigeMeta, setCorrigeMeta] = useState(false);
  const [corrigeIntitule, setCorrigeIntitule] = useState(false);
  const [gelJusqu, setGelJusqu] = useState('');
  const [degelMotif, setDegelMotif] = useState<string | null>(null);
  /* GED-4 b — retrait d'un destinataire d'une diffusion déjà émise. On pouvait
     ajouter quelqu'un à la liste, jamais l'en enlever.

     Le motif est OBLIGATOIRE côté serveur (la route refuse sans) : le panneau
     le demande donc en toutes lettres et le bouton reste inerte tant qu'il est
     vide, plutôt que de laisser partir un appel qui reviendra en erreur.

     Un seul état pour les deux gestes — retirer la ligne, ou la basculer en
     « pour information » — parce que c'est le SERVEUR qui tranche lequel est
     légitime : un accusé déjà signé ne s'efface pas, et le panneau doit dire
     cela AVANT le clic, pas après le refus. */
  const [retraitCible, setRetraitCible] = useState<string | null>(null);
  const [retraitMotif, setRetraitMotif] = useState('');
  const { run: runFiche, estOccupe: occupeFiche } = useBusy();

  useEffect(() => { if (doc) void loadJournal(doc.code); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [doc?.code]);

  // Chargé À L'OUVERTURE de l'onglet Versions seulement : la question ne se pose
  // pas ailleurs, et la réponse se lit sur le disque (un appel par fiche).
  useEffect(() => {
    if (docTab !== 'versions' || !doc) return;
    let vivant = true;
    void fetch(`/api/ged/ged/docs/${doc.documentId}/versions`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivant) setArchivees(Array.isArray(d?.archivees) ? d.archivees.map(String) : []); })
      .catch(() => { if (vivant) setArchivees([]); });
    return () => { vivant = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [docTab, doc?.documentId]);

  const current = useMemo(() => (doc?.versions || []).find((v) => v.dateVigueur && !v.obsolete) || (doc?.versions || [])[0] || null, [doc]);

  // Trace l'ouverture effective du document par un destinataire : c'est ce que
  // le serveur exige avant d'accepter un accusé de lecture (constat QA MAJ-2).
  // Le ref évite de rejouer l'appel après le refresh déclenché par act().
  //
  // Ces deux hooks vivaient APRÈS le retour anticipé « document introuvable » :
  // ils n'étaient donc appelés que sur les rendus où `doc` existe, ce que React
  // refuse (« Rendered more hooks than during the previous render ») dès que
  // l'on quitte une fiche. Ils remontent ici, et gardent sur `doc`.
  const maDiffusion = doc ? (doc.diffusion || []).find((x) => x.userId === session.key) : undefined;
  const openedRef = useRef<string | null>(null);
  //
  // Le gel (GED-5) passe par le MÊME appel : ouvrir le document est ce qui vaut
  // « j'en ai pris connaissance » et fait disparaître la tâche. La condition ne
  // pouvait donc plus s'arrêter à la première ouverture — un destinataire ayant
  // déjà accusé réception n'aurait jamais été compté comme informé du gel.
  const gelActif = doc?.gel?.actif ? doc.gel : null;
  const gelAPrendreEnCompte = !!(gelActif && maDiffusion && String(maDiffusion.gelVuLe || '') < gelActif.depuis);
  useEffect(() => {
    if (!doc || !maDiffusion) return;
    const premiereOuverture = !maDiffusion.dateAck && !maDiffusion.dateOuverture;
    if (!premiereOuverture && !gelAPrendreEnCompte) return;
    const marque = `${doc.documentId}|${gelActif?.depuis || ''}`;
    if (openedRef.current === marque) return;
    openedRef.current = marque;
    void act(doc.documentId, 'open');
  }, [doc, maDiffusion, act, gelActif?.depuis, gelAPrendreEnCompte]);

  // Même correction de libellé qu'en tête de fiche : ce bouton mène au
  // catalogue, il ne revient pas d'où l'on vient.
  /* FAUX VIDE AU CHARGEMENT (RQ, 04/10/2026) : en ouvrant /documents/EN-ORG-001
     par son adresse, la fiche affirmait « Document introuvable » pendant les
     quelques secondes où le catalogue arrivait — et on l'a pris pour un refus
     de périmètre. Tant que le catalogue n'est pas là, on ne sait pas. */
  if (!doc && enChargement(booted, loading, docs.length)) return <Skeleton rows={4} />;
  if (!doc) return <p style={{ color: 'var(--text-muted)' }}>Aucun document {selectedCode ? <>ne porte le code <strong>{selectedCode}</strong> </> : null}dans le catalogue. <Bouton taille="sm" onClick={() => go('list')}>Retour à la liste des documents</Bouton></p>;

  const m = stateMeta(doc.statut);
  /* LES MENTIONS DE L'IMPRESSION ÉCRAN viennent du même paramétrage que celles
     du PDF (arbitrage RQ du 09/10/2026). Avant, elles étaient écrites en dur
     ici : reformuler la mention au paramétrage ne valait que pour le PDF, et le
     papier sorti par le navigateur disait autre chose.
     Ni `numero` ni `lieu` : cette sortie ne passe JAMAIS par le registre des
     copies maîtrisées — elle est non maîtrisée par construction. */
  const valeursMention = {
    date: fmtDate(new Date().toISOString()),
    par: session.name,
    code: doc.code,
    titre: doc.titre || doc.code,
    version: String(doc.versionCourante || ''),
    statut: m.label,
    labo: settings?.branding?.labName || 'BIO24 Alliance',
    dateVigueur: doc.dateVigueur ? fmtDate(doc.dateVigueur) : '',
  };
  /* LE FILIGRANE DE L'IMPRESSION ÉCRAN, en DÉFAUT-REFUS (RQ, 10/10/2026).
     La liste était `['retire', 'obsolete']` : un document à vérifier, à
     approuver, ou un simple brouillon sortait de l'imprimante SANS aucune
     mention — la page avait l'allure exacte du document opposable, ce que le PDF
     interdit depuis le 18/08/2026. On énumère donc ce qui s'imprime NU, et tout
     le reste est barré : un statut neuf sera couvert sans qu'on y pense.
     ⚠️ Les statuts EXTERNES n'y figurent pas, et ce n'est pas un oubli : on ne
     tamponne pas un référentiel acheté (même règle que `apercuExterne()` au
     serveur). Cette zone n'imprime de toute façon que le contenu rédigé ici. */
  const STATUTS_EXTERNES = ['enregistre', 'adopte', 'remplace'];
  const situationFiligrane: SituationMention | null =
    doc.statut === 'envigueur' || STATUTS_EXTERNES.includes(doc.statut) ? null
      : ['averifier', 'aapprouver'].includes(doc.statut) ? 'enValidation'
        : 'nonApplicable';
  const roles = session.roles;
  const journal = events[doc.code] || [];
  const myDiff = maDiffusion;
  const monLecture = monEtatLecture(doc, session.key);

  // Rédacteurs affectés (liste `redacteurs`, repli sur l'ancien scalaire) —
  // règle du module : l'édition est réservée aux affectés et au RQ.
  const affectes = (doc.redacteurs?.length ? doc.redacteurs : [doc.redacteur]).filter(Boolean) as string[];
  const brouillonDe = (doc.versions || []).find((v) => v.brouillon)?.redacteur;
  const peutRediger = roles.includes('rq')
    || (roles.includes('redacteur') && (affectes.includes(session.key) || brouillonDe === session.key));

  // actions contextuelles (statut × rôles)
  /* `refus` : le bouton reste VISIBLE mais grisé, et la phrase dit pourquoi
     (demande de la RQ du 03/10/2026 — un bouton caché ne dit à personne qu'il
     existe ni à qui s'adresser). */
  /* ── LE REGISTRE D'UN BOUTON (07/10/2026) ───────────────────────────────
     Signalé par la RQ : sur IT-PRE-003, Asma Ezzi devait à la fois ACCUSER
     RÉCEPTION de la v1 applicable et TERMINER LA RÉDACTION de la v2 en
     révision. Les deux familles de boutons s'affichaient ensemble, et il
     devenait impossible de savoir sur quelle version on agissait — « je me
     mélange les pinceaux ».

     La cause est structurelle : ce tableau était alimenté par onze `if` qui ne
     regardent que `doc.statut` et les rôles, JAMAIS la version consultée. Or
     `enrevision` figure dans deux listes à la fois (le gel lit
     `['envigueur','enrevision']`, la rédaction `['brouillon','enrevision']`) et
     « Accuser réception » ne dépend pas du statut du tout — il dépend de ma
     ligne de diffusion, qui porte sur la version APPLICABLE. La collision était
     donc inévitable sur ce seul statut.

     Chaque bouton déclare désormais sur QUOI il agit :
      · `applicable` — ce qui s'applique ce matin (accuser réception, retirer en
        urgence, geler, déclencher une révision) ;
      · `chantier`   — la version candidate (rédiger, terminer la rédaction,
        vérifier, approuver) ;
      · `fiche`      — la fiche elle-même, hors version (abandonner, reprendre,
        et les actes d'un document externe, qui n'a pas de circuit de révision).
     `fiche` s'affiche sur les deux faces : ce n'est pas un acte de version. */
  type Registre = 'applicable' | 'chantier' | 'fiche';
  const actions: { label: string; icon: string; onClick: () => void | Promise<void>; variant: 'primary' | 'outline' | 'danger'; refus?: string | null; registre: Registre }[] = [];
  if (['brouillon', 'enrevision'].includes(doc.statut) && peutRediger) {
    // Le document se rédige DANS SON FICHIER (décision RQ du 29/07) : l'éditeur
    // de contenu structuré interne a été retiré. Il ouvrait un second lieu
    // d'écriture pour le même document, dont le contenu ne se retrouvait ni
    // dans le .docx ni dans l'aperçu imprimé — deux textes, une seule version.
    actions.push({
      label: doc.fichier ? 'Éditer le fichier' : 'Rédiger le document',
      icon: 'pen', onClick: () => setDocTab('fichier'), variant: 'primary', registre: 'chantier',
    });
    // Fin de rédaction depuis la FICHE : le bouton « Soumettre » n'existait que
    // dans l'éditeur de contenu interne, et il y écrase d'abord le brouillon
    // avec le HTML de cet éditeur — inutilisable pour un document rédigé dans
    // OnlyOffice (le fichier Word aurait été doublé d'un contenu vide). Ici on
    // force l'enregistrement du fichier bureautique, puis on soumet.
    // Le RQ peut rédiger et soumettre TOUT document en rédaction, même sans y
    // être affecté (ged.ts `peutRediger`) : c'est le passe-droit qui lui permet
    // de débloquer un circuit. Mais l'écran présentait le même bouton dans les
    // deux cas — on distingue donc l'acte, qui n'engage pas la même personne.
    const enTantQueRQ = !affectes.includes(session.key);
    actions.push({
      label: enTantQueRQ ? 'Terminer la rédaction (RQ)' : 'Terminer la rédaction', icon: 'send', variant: 'primary', registre: 'chantier',
      onClick: async () => {
        const avertissement = enTantQueRQ
          ? 'Vous n’êtes pas rédacteur affecté à ce document : vous soumettez au titre du RQ, et c’est votre nom qui sera inscrit au journal.\n\n'
          : '';
        if (!(await demander(`${avertissement}Soumettre à vérification ? Le contenu est figé : plus aucune modification ne sera possible avant un éventuel renvoi.`, { confirmer: 'Soumettre' }))) return;
        if (doc.fichier) {
          // Dernières frappes encore côté Document Server : on les fait écrire
          // avant de figer, sinon le vérificateur relit une version tronquée.
          await fetchEcriture(`/api/ged/ged/docs/${doc.documentId}/office/flush`, { method: 'POST', credentials: 'include' }).catch(() => {});
        }
        void act(doc.documentId, 'submit');
      },
    });
  }
  /* ── DOCUMENT EXTERNE : adopter, remplacer ─────────────────────────────
     Ces deux actes vivaient dans un encadré au milieu de la fiche. La RQ l'a
     dit le 14/08 : « le bouton doit être là où j'ai l'habitude de valider la
     rédaction ». C'est juste — ce sont les MÊMES gestes dans son travail :
     « je prononce quelque chose sur ce document ». Les ranger ailleurs
     obligeait à apprendre un second endroit pour un seul type de document.
     L'encadré explicatif reste sous l'aperçu ; seuls les boutons montent. */
  if (String(doc.docType || '').toUpperCase() === 'DE' && roles.includes('rq')) {
    if (doc.statut !== 'adopte' && doc.statut !== 'remplace') {
      actions.push({
        label: 'Adopter cette édition', icon: 'check', variant: 'primary', registre: 'fiche',
        onClick: async () => {
          if (!(await demander(
            `Adopter ${doc.versionCourante || 'cette édition'} de « ${doc.titre} » ?\n\n`
            + 'Le laboratoire déclare s’y référer : c’est l’édition contre laquelle il s’auditera, '
            + 'et celle que citeront les dossiers figés à partir d’aujourd’hui.',
            { titre: 'Adopter un document externe', confirmer: 'Adopter' },
          ))) return;
          await appelerParcoursExterne(doc.documentId, 'adopter', {}, setErreurExterne, refresh);
        },
      });
    }
    if (doc.statut === 'adopte') {
      actions.push({
        label: 'Marquer comme remplacée', icon: 'refresh', variant: 'outline', registre: 'fiche',
        onClick: () => setRemplacementMotif(''),
      });
    }
  }

  // Le circuit de revue est un AUTRE écran (`go('review')`) : depuis le panneau,
  // il fait disparaître la superposition. Le geste est pourtant celui qu'on vient
  // chercher (RQ, 03/10/2026 : le vérificateur ouvrait la carte du tableau de
  // bord et ne trouvait pas « Vérifier le document »). Il est donc OFFERT aussi
  // en panneau : il bascule d'abord en pleine page — le fil d'Ariane ramène à
  // l'écran d'origine — puis ouvre la revue.
  if (['averifier', 'aapprouver'].includes(doc.statut) && (roles.includes('verificateur') || roles.includes('approbateur') || roles.includes('rq')))
    /* Le bouton porte le GESTE, pas le nom de l'écran (RQ, 03/10/2026) : le
       vérificateur désigné cherchait « Vérifier le document » et trouvait
       « Circuit de revue ». Qui n'est pas attendu pour signer (RQ de passage,
       signataire d'une autre étape) garde le nom neutre. */
    actions.push({
      label: ({ verification: 'Vérifier le document', approbation: 'Approuver le document' } as Record<string, string>)[attendDeMoi(doc, session) || ''] || 'Circuit de revue',
      icon: 'shield', variant: 'primary', registre: 'chantier',
      onClick: () => { if (enPanneau) panneauEnPleinePage(); go('review'); },
    });
  if (doc.statut === 'envigueur' && roles.includes('rq') && !gelActif) {
    actions.push({ label: 'Déclencher une révision', icon: 'refresh', onClick: () => setRevisionMotif(''), variant: 'outline', registre: 'applicable' });
  }
  // Gel de l'applicabilité (GED-5) — RQ, comme le retrait d'urgence. Le bouton
  // ne s'affiche que sur un document applicable : ailleurs il n'y a rien à figer.
  if (['envigueur', 'enrevision'].includes(doc.statut) && roles.includes('rq')) {
    actions.push(gelActif
      ? { label: 'Lever le gel', icon: 'refresh', onClick: () => setDegelMotif(''), variant: 'outline', registre: 'applicable' }
      : { label: 'Geler l’applicabilité', icon: 'lock', onClick: () => { setGelJusqu(''); setGelMotif(''); }, variant: 'outline', registre: 'applicable' });
  }
  /* ── RETIRER EN URGENCE, DEPUIS LA FICHE (03/10/2026) ──────────────────
     Jusqu'ici le retrait ne se faisait que depuis l'écran « Retrait
     d'urgence », masqué à qui n'est pas RQ. Il est offert ici à TOUS les
     lecteurs d'un document en vigueur ; qui n'a pas la famille d'actes A7
     (« Abandonner, suspendre ou reprendre un document ») voit le bouton grisé
     avec la phrase du moteur. Le serveur garde son refus quoi qu'affiche
     l'écran. Repli, moteur antérieur sans `actes` : la règle d'avant (RQ). */
  if (doc.statut === 'envigueur') {
    const refusRetrait = session.actes
      ? (session.actes.A7 ?? null)
      : (roles.includes('rq') ? null : 'Le retrait d’urgence est réservé au responsable qualité.');
    actions.push({
      label: 'Retirer', icon: 'alert', variant: 'danger', refus: refusRetrait, registre: 'applicable',
      onClick: () => setUrgenceMotif(''),
    });
  }
  /* ── ABANDONNER UNE FICHE JAMAIS DIFFUSÉE (14/08/2026) ──────────────────
     Demandé par la RQ : une fiche ouverte par erreur restait au fonds pour
     toujours. Le bouton ne s'affiche que là où l'acte est possible — jamais
     sur un document qui a circulé, où la réponse serait un refus. */
  if (roles.includes('rq') && ['demande', 'brouillon', 'enregistre'].includes(doc.statut)
      && !((doc.diffusion || []).length)) {
    actions.push({ label: 'Abandonner la fiche', icon: 'trash', onClick: () => setAbandonMotif(''), variant: 'outline', registre: 'fiche' });
  }
  /* Contrepartie de l'abandon. Sans ce bouton, « abandonné » serait un état
     sans issue — et le geste de reprise se ferait alors par la bande, en
     adoptant ou en soumettant une fiche mise de côté, ce qui laisserait au
     journal un enchaînement que personne ne saurait relire. */
  if (roles.includes('rq') && doc.statut === 'abandonne') {
    actions.push({
      label: 'Reprendre la fiche', icon: 'refresh', variant: 'primary', registre: 'fiche',
      onClick: () => void runFiche('reprendre', async () => {
        setErreurExterne('');
        const r = await fetchEcriture(`/api/ged/ged/docs/${doc.documentId}/reprendre`, { method: 'POST', credentials: 'include' });
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          setErreurExterne(j?.error?.message || 'Reprise refusée.');
          return;
        }
        await refresh();
      }),
    });
  }

  if (myDiff && myDiff.requis && !myDiff.dateAck)
    actions.push({ label: 'Accuser réception', icon: 'check', onClick: () => void act(doc.documentId, 'ack'), variant: 'primary', registre: 'applicable' });
  // Le bouton « Imprimer » de la fiche a été RETIRÉ le 30/07.
  //
  // Il déclenchait `window.print()` sur `#ged-print-area`, qui ne rend que
  // l'ancien contenu structuré — vide depuis que les documents se rédigent dans
  // leur fichier bureautique. Il était donc masqué sur tout le fonds réel, et
  // là où il apparaissait encore il produisait un cartouche sans contenu : une
  // feuille portant la référence et la version d'un document maîtrisé, mais pas
  // son texte. Sur un fonds ISO, ce papier-là est pire que pas de papier.
  //
  // L'impression passe maintenant par deux chemins explicites, qui ne se
  // confondent plus : « Télécharger / imprimer » dans l'aperçu (copie NON
  // maîtrisée, pour consulter) et « Émettre un exemplaire » dans le registre
  // des copies papier (copie maîtrisée numérotée, pour afficher).

  const nomsRedacteurs = Array.from(new Set([...(doc.redacteurs || []), doc.redacteur].filter(Boolean) as string[])).map((k) => userName(settings, k)).join(', ');

  /* ── « JE PEUX CLIQUER DESSUS POUR LA VOIR SI MES DROITS ME LE PERMETTENT »
     Trois formes, et la troisième est celle qu'on oublie :
      · on lit l'applicable et j'ai le droit → le lien qui ouvre le chantier ;
      · on lit le chantier → le chemin de retour vers ce qui s'applique ;
      · je n'ai pas le droit → la mention reste, inerte, et la PHRASE est
        ÉCRITE (règle du 03/10 : un geste refusé se dit avant le clic, pas
        seulement en infobulle, et un lien absent n'apprend rien à personne).
     En RÉDACTION il n'existe pas d'aperçu officiel du brouillon — ce serait un
     document d'allure maîtrisée qui n'a franchi aucune étape : le lien ouvre
     alors l'onglet Fichier, là où le texte se travaille réellement. */
  const styleLienMention: React.CSSProperties = {
    background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit',
    textDecoration: 'underline', cursor: 'pointer', fontWeight: 700,
  };
  const retourApplicable = (
    <button type="button" className="ged-btn" style={styleLienMention}
      onClick={() => { setVoirEnCours(false); choisirVersion(null); setDocTab('lecture'); }}>
      Revenir à la version applicable (v{numApplicable})
    </button>
  );
  const lienVersionEnCours = !revision ? null : voirEnCours ? retourApplicable : accesEnCours.ouvert ? (
    <button type="button" className="ged-btn" style={styleLienMention}
      title={etapeCand === 'redaction'
        ? 'Ouvrir le fichier de travail de la nouvelle version — elle n’est pas applicable.'
        : 'Ouvrir l’aperçu de la nouvelle version — grisé et filigrané : elle n’est pas applicable.'}
      onClick={() => { choisirVersion(null); setVoirEnCours(true); setDocTab(etapeCand === 'redaction' ? 'fichier' : 'lecture'); }}>
      Consulter la version en cours (v{versionCand?.num})
    </button>
  ) : (
    <span style={{ fontWeight: 400 }}>{accesEnCours.refus}</span>
  );

  /* ── CE QUE LA FACE COURANTE AUTORISE ───────────────────────────────────
     Hors révision il n'y a rien à séparer : un `brouillon` n'a pas de version
     applicable, un `envigueur` n'a pas de candidate, et filtrer ferait
     disparaître des boutons légitimes. La séparation ne vaut donc QUE pendant
     une révision — exactement la situation qui mélangeait les deux registres.

     ⚠️ On lit `voirEnCours` et non `doc.statut` : c'est la question « que suis-je
     en train de regarder ? », la seule qui décide ce sur quoi je peux agir. */
  const faceCourante: Registre = voirEnCours ? 'chantier' : 'applicable';
  const actionsVues = revision
    ? actions.filter((a) => a.registre === 'fiche' || a.registre === faceCourante)
    : actions;

  /* ── « QU'ON SE RENDE COMPTE EN UN COUP D'ŒIL » (RQ, 07/10/2026) ─────────
     La phrase du bandeau ne suffisait pas : on la lit une fois, puis on
     travaille. Le signal doit tenir dans le coin de l'œil, pendant tout le
     temps passé sur la fiche — d'où le cadre coloré autour du contenu et le
     bandeau d'avertissement au-dessus des onglets, qui ne se lisent pas, qui se
     VOIENT. Deux cas distincts, et le mot « applicable » dans les deux :
      · le chantier, ambre — pas encore validé, il le sera peut-être ;
      · une archive, gris — validé autrefois, périmé depuis. */
  const faceNonApplicable: { teinte: string; fond: string; mot: string } | null =
    litUneArchive
      ? { teinte: 'var(--text-muted)', fond: 'var(--gray-100)', mot: `Version obsolète (v${versionChoisie}) — ne pas appliquer` }
      : revision && voirEnCours
        ? { teinte: 'var(--amber-600)', fond: 'var(--amber-50)', mot: `Version non validée (v${numCandidat}) — ${libelleEtape(etapeCand)}, ne pas appliquer` }
        : null;

  const banner =
    // Le gel passe AVANT la révision : c'est lui qui explique pourquoi le
    // document n'avance plus. Après le retrait, qui reste le fait le plus grave.
    gelActif && doc.statut !== 'retire' ? { bg: 'var(--cyan-50)', fg: 'var(--cyan-700)', text: `❄ Applicabilité gelée depuis le ${fmtDate(gelActif.depuis)} — la v${doc.versionCourante} reste applicable et imprimable, son évolution est suspendue${gelActif.jusqu ? ` jusqu’au ${fmtDate(gelActif.jusqu)}` : ''}. Motif : ${gelActif.motif}` }
    : doc.statut === 'retire' ? { bg: 'var(--red-50)', fg: 'var(--red-600)', text: `⚠ Retiré d’urgence — ne pas appliquer. Motif : ${doc.retraitMotif || '—'}` }
    /* ── ON LIT UNE ARCHIVE (06/10/2026) ───────────────────────────────────
       Ce bandeau passe AVANT le gel et la révision : quand on a demandé une
       version passée, c'est LE fait qui compte — le reste décrit l'état du
       document, pas ce que l'écran montre. Il ne passe pas avant le retrait
       d'urgence : « ne pas appliquer » ne cède la place à rien. */
    : litUneArchive ? {
      bg: 'var(--gray-100)', fg: 'var(--text-strong)',
      text: `Vous consultez la v${versionChoisie}, obsolète — exemplaire archivé, conservé tel qu’il faisait foi.${numApplicable ? ` La version applicable est la v${numApplicable}.` : ''}`,
      lien: numApplicable ? retourApplicable : null,
    }
    /* ── LA MENTION (RQ, 06/10/2026) ───────────────────────────────────────
       Elle vaut pour TOUTE la durée de la révision — rédaction, vérification,
       approbation — et pas seulement pour le statut `enrevision` : ce qui
       compte est qu'une version s'applique pendant qu'une autre se prépare.
       Elle dit les DEUX numéros, l'étape où en est le chantier, et porte le
       lien qui l'ouvre (`lien` ci-dessous). */
    : revision ? {
      bg: 'var(--amber-50)', fg: 'var(--amber-600)',
      text: voirEnCours
        ? `Vous consultez la v${versionCand?.num} — ${libelleEtape(etapeCand)}, non applicable. La version applicable est la v${versionAppl?.num}.`
        : `La v${versionAppl?.num} s’applique. Une nouvelle version (v${versionCand?.num}) est ${libelleEtape(etapeCand)}.`,
      lien: lienVersionEnCours,
    }
    /* Le bandeau dit OÙ en est le document, pas seulement qu'il est figé (RQ,
       04/10/2026) : « rédigé » est l'information qu'on cherche en arrivant. */
    : doc.statut === 'averifier' ? { bg: 'var(--cyan-50)', fg: 'var(--cyan-700)', text: `✓ Rédigé${nomsRedacteurs ? ` par ${nomsRedacteurs}` : ''} — en attente de vérification${doc.verificateur ? ` par ${userName(settings, doc.verificateur)}` : ''}. Contenu figé — document en circuit de revue.` }
    : doc.statut === 'aapprouver' ? { bg: 'var(--cyan-50)', fg: 'var(--cyan-700)', text: `✓ Rédigé${nomsRedacteurs ? ` par ${nomsRedacteurs}` : ''}${doc.verificateur ? ` et vérifié par ${userName(settings, doc.verificateur)}` : ''} — en attente d’approbation${doc.approbateur ? ` par ${userName(settings, doc.approbateur)}` : ''}. Contenu figé — document en circuit de revue.` }
    : null;

  const nbLiens = liens.filter((l) => (l.sourceType === 'document' && l.sourceRef === doc.code) || (l.cibleType === 'document' && l.cibleRef === doc.code)).length;

  // `externe` (type DE) et non `classe` : « importé » dit le format du fichier, pas l'origine.
  /* Pendant une révision, le fichier de travail EST le brouillon de la version
     suivante : il ne s'ouvre qu'à ceux qui y ont droit (le serveur refuse les
     autres — `office.ts`, `verrou.ts`). Ce qui s'applique reste lisible par
     tous : c'est l'aperçu, servi depuis l'instantané de la version en vigueur. */
  const fichierVisible = (externe || !['averifier', 'aapprouver'].includes(doc.statut))
    && (!revision || accesEnCours.ouvert)
    && (externe || voitSource);
  const tabs: { key: typeof docTab; label: string }[] = [
    // Pendant une révision, « Aperçu » tout court ne dit pas LAQUELLE des deux
    // versions on lit — c'est la seule information qui compte à cet instant.
    { key: 'lecture', label: revision || litUneArchive ? `Aperçu (v${versionVue})` : 'Aperçu' },
    /* L'onglet Fichier DISPARAÎT une fois le document rédigé (RQ, 04/10/2026) :
       en circuit il n'y a plus rien à y faire depuis la fiche — on lit l'aperçu
       (grisé), et le vérificateur / l'approbateur ouvrent le fichier en révision
       depuis l'écran de revue, qui l'embarque. Un document externe garde le sien :
       il n'a pas de circuit. */
    ...(fichierVisible ? [{ key: 'fichier' as const, label: doc.fichier ? `Fichier (.${doc.fichier.ext})` : 'Fichier' }] : []),
    { key: 'versions', label: `Versions (${(doc.versions || []).length})` },
    { key: 'diffusion', label: `Diffusion (${(doc.diffusion || []).length})` },
    // Le document était la seule entité liable sans onglet Liens : une NC, un
    // audit ou un équipement pouvait pointer vers lui, mais depuis sa fiche on
    // ne voyait rien — et rien ne s'y rattachait.
    { key: 'liens', label: `Liens (${nbLiens})` },
    { key: 'comments', label: `Commentaires (${(doc.commentaires || []).length})` },
  ];

  const ackSummary = (() => {
    const req = (doc.diffusion || []).filter((x) => x.requis);
    const ok = req.filter((x) => x.dateAck).length;
    return req.length ? `${ok}/${req.length} accusés requis` : 'aucun accusé requis';
  })();
  /* Lot GED-4 — la relance n'existait QUE sur l'écran « Diffusion & accusés ».
     Or c'est en regardant la fiche d'un document qu'on constate qu'un lecteur
     n'a pas accusé réception : il fallait quitter la fiche, retrouver le
     document dans une autre liste, et relancer là-bas. La route serveur
     (`POST /ged/docs/:id/relance`) est la même : c'était un manque d'interface. */
  const attenteAck = (doc.diffusion || []).filter((x) => x.requis && !x.dateAck).length;
  const relancesTotal = (doc.diffusion || []).reduce((n, x) => n + (x.relances || 0), 0);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {confirmation}
      {/* zone d'impression maîtrisée — jamais en panneau : `#ged-print-area` est
          un identifiant UNIQUE, visé tel quel par la feuille d'impression. Deux
          exemplaires dans la page (celui de l'écran, celui du panneau) et c'est
          le mauvais document qui part à l'imprimante. */}
      {!enPanneau && (
      <div id="ged-print-area" style={{ display: 'none' }}>
        {/* Une copie papier non applicable ne doit jamais pouvoir passer pour
            applicable → filigrane visible uniquement à l'impression. Les phrases
            viennent du paramétrage, comme celles du PDF (09/10/2026), et UNE
            SEULE sert aux deux états non applicables : l'écran distinguait
            « retiré d'urgence » d'« obsolète », le PDF non, et deux textes pour
            une même interdiction en font fatalement diverger un.
            Le tiret cadratin devient un RETOUR À LA LIGNE : la pastille est en
            68 px, et « EN COURS DE VALIDATION — NE PAS APPLIQUER » d'un seul
            tenant déborderait de la page. C'est aussi la mise en page qu'avaient
            les deux phrases en dur. */}
        {situationFiligrane && (
          <div className="print-void" aria-hidden="true">
            <span>{mentionDe(settings?.branding?.mentions, situationFiligrane, valeursMention).replace(/\s+—\s+/g, '\n')}</span>
          </div>
        )}
        <div style={{ fontFamily: 'var(--font-sans)', color: 'var(--text-strong)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #0E2740', paddingBottom: 8, marginBottom: 14 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{settings?.branding?.labName || 'BIO24 Alliance'}</div>
              {/* ALIGNÉ SUR LE PDF (arbitrage RQ du 09/10/2026) : la mention seule.
                  Le « Référentiel » (ISO 15189 · TUNAC) était sorti de l'en-tête
                  de l'aperçu officiel le 03/10 et avait survécu ici : un même
                  document portait deux en-têtes différents selon qu'on l'imprimait
                  par le PDF ou par le navigateur. Le champ reste au paramétrage —
                  il sert au compte-rendu de revue de direction. */}
              <div style={{ fontSize: 12.5 }}>{settings?.branding?.mention || ''}</div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
              {settings?.branding?.cartouche?.reference !== false && <div>{doc.code}</div>}
              {settings?.branding?.cartouche?.version !== false && <div>v{doc.versionCourante} — {m.label}</div>}
              {settings?.branding?.cartouche?.date !== false && <div>{fmtDate(doc.dateVigueur)}</div>}
            </div>
          </div>
          <h1 style={{ fontSize: 19, margin: '0 0 4px' }}>{doc.titre}</h1>
          <div style={{ fontSize: 12.5, marginBottom: 14 }}>
            Rédigé par {affectes.map((k) => userName(settings, k)).join(', ')} · Vérifié par {userName(settings, doc.verificateur)} · Approuvé par {userName(settings, doc.approbateur)}
          </div>
          <div className="ged-read" dangerouslySetInnerHTML={{ __html: current?.contenu || '' }} />
          {/* Même code couleur que l'aperçu officiel (RQ, 06/10/2026) : la mention
              de copie non maîtrisée est ROUGE. Une impression écran ne passe
              jamais par le registre des copies — elle n'a donc pas de variante
              verte, contrairement au PDF (`backend/src/cartouche-docx.ts`). */}
          <div style={{ borderTop: '1px solid #999', marginTop: 18, paddingTop: 8, fontSize: 12.5 }}>
            {settings?.branding?.footer || ''} — <span style={{ color: 'var(--red-600)', fontWeight: 700 }}>{mentionDe(settings?.branding?.mentions, 'copieNonMaitrisee', valeursMention)}</span>
          </div>
        </div>
      </div>
      )}

      <div className="no-print" style={{ display: 'grid', gap: 16 }}>
        <div>
          {/* Ce bouton ne fait PAS un retour, et son ancien libellé le laissait
              croire (question de la RQ, 14/08/2026) : il mène TOUJOURS au
              catalogue, y compris quand on est arrivé ici depuis le tableau de
              bord, la recherche ou une demande — c'est-à-dire vers un écran où
              l'on n'était pas. Depuis que la flèche « précédent » existe dans
              l'en-tête, garder deux gestes qui se ressemblent sans faire la
              même chose est un piège. On garde le raccourci — aller choisir un
              autre document est fréquent — mais il dit désormais où il va.

              En panneau il est masqué : il quitterait la superposition, alors
              que le panneau se referme par sa propre croix. */}
          {!enPanneau && (
          <button onClick={() => go('list')} className="ged-btn"
            title="Ouvrir la liste des documents. Pour revenir d’où vous venez, utilisez la flèche « précédent » en haut de l’écran."
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'var(--font-sans)' }}>
            <I n="doc" s={15} />Retour à la liste des documents
          </button>
          )}
          <JalonsDocument doc={doc} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
            {/* ── LA COULEUR DU TYPE (RQ, 07/09/2026) ──────────────────────
                Le même repère que dans la liste, au même endroit de l'œil : on
                ouvre un document et l'on retrouve la barre qu'on suivait.
                ⚠️ Le LIBELLÉ est écrit à côté, il n'est pas remplacé par la
                couleur — sur la fiche il y a la place, et une couleur seule
                n'est pas une information. */}
            {(() => {
              const t = (settings?.docTypes || []).find((x) => x.code === doc.docType);
              return (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <span aria-hidden style={{ width: 4, height: 20, borderRadius: 'var(--radius-xs)', background: t?.color || 'var(--border-default)' }} />
                  <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>
                    {t?.label || doc.docType}
                  </span>
                </span>
              );
            })()}
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 17, fontWeight: 700, color: 'var(--navy-900)' }}>{doc.code}</span>
            <IconeApercu doc={doc} taille={18} />
            <BoutonJournal reference={doc.code} quoi="de ce document" />
            {/* ⚠️ `doc.versionCourante` est l'APPLICABLE, quoi qu'on regarde :
                en consultant la révision, l'en-tête affichait donc le numéro de
                l'autre version — juste à côté du titre, à l'endroit même où l'on
                vérifie sur quoi l'on travaille. Il dit maintenant la version
                VUE, et le dit en ambre quand elle ne s'applique pas. */}
            {faceNonApplicable ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 11px', background: faceNonApplicable.fond, color: faceNonApplicable.teinte }}>
                <I n="alert" s={13} />v{versionVue} · non applicable
              </span>
            ) : (
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-muted)' }}>v{doc.versionCourante}</span>
            )}
            <StatePill statut={doc.statut} />
            {/* Indicateur de MA lecture : « Lu le … » une fois l'accusé donné,
                « À accuser » tant que je ne l'ai pas fait. Persiste après
                l'accusé, là où le bouton « Accuser réception » disparaît. */}
            {monLecture && (
              monLecture.etat === 'lu'
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 11px', background: 'var(--green-50)', color: 'var(--green-600)' }}><I n="check" s={13} sw={2.4} />Lu le {fmtDate(monLecture.dateAck)}</span>
                : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '3px 11px', background: 'var(--amber-50)', color: 'var(--amber-600)' }}><I n="clock" s={13} />Lecture à accuser</span>
            )}
            <span style={{ flex: 1 }} />
            {actionsVues.map((a) => (
              <Bouton key={a.label} variante={a.variant === 'primary' ? 'primaire' : a.variant === 'danger' ? 'danger' : 'contour'}
                onClick={a.onClick} disabled={!!a.refus} title={a.refus || undefined}>
                <I n={a.icon} s={14} />{a.label}
              </Bouton>
            ))}
          </div>
          {/* La phrase d'un bouton grisé s'ÉCRIT, elle ne se cache pas dans une
              infobulle : au toucher, et au clavier, il n'y a pas de survol. */}
          {actionsVues.filter((a) => a.refus).map((a) => (
            <div key={`refus-${a.label}`} style={{ marginTop: 8, fontSize: 12.5, color: 'var(--text-muted)', textAlign: 'end' }}>
              <strong>{a.label}</strong> — {a.refus}
            </div>
          ))}
          {erreurExterne && (
            <div style={{ ...card, padding: '10px 14px', marginTop: 12, fontSize: 12.5, color: 'var(--red-600)', lineHeight: 1.5 }}>
              {erreurExterne}
            </div>
          )}
          {abandonMotif !== null && (
            <div style={{ ...card, padding: 14, marginTop: 12, display: 'grid', gap: 8 }}>
              <label htmlFor="abandon-motif" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary-active)' }}>
                Motif de l’abandon (facultatif)
              </label>
              <textarea id="abandon-motif" autoFocus rows={2} value={abandonMotif}
                onChange={(e) => setAbandonMotif(e.target.value)}
                placeholder="Ex. fiche ouverte en double — le document est suivi sous PR-QUAL-002."
                style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                La fiche n’est pas détruite : elle sort des listes et conserve son journal. Rien de ce
                qui a été déposé ou tracé ne disparaît — c’est ce qui permettra, plus tard, de dire
                pourquoi ce code a existé.
              </div>
              <PiedActions taille="sm" annuler={{ onClick: () => { setAbandonMotif(null); setErreurExterne(''); } }} principale={{ libelle: 'Abandonner la fiche', libelleChargement: 'Abandon…', chargement: occupeFiche('abandon'), disabled: occupeFiche(), onClick: () => void runFiche('abandon', async () => {
                  setErreurExterne('');
                  const r = await fetchEcriture(`/api/ged/ged/docs/${doc.documentId}/abandonner`, {
                    method: 'POST', credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ motif: abandonMotif.trim() }),
                  });
                  if (!r.ok) {
                    const j = await r.json().catch(() => ({}));
                    setErreurExterne(j?.error?.message || 'Abandon refusé.');
                    return;
                  }
                  setAbandonMotif(null);
                  await refresh();
                }) }} />
            </div>
          )}
          {remplacementMotif !== null && (
            <div style={{ ...card, padding: 14, marginTop: 12, display: 'grid', gap: 8 }}>
              <label htmlFor="ext-remp" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary-active)' }}>
                Quelle édition la remplace ?
              </label>
              <textarea id="ext-remp" autoFocus rows={2} value={remplacementMotif}
                onChange={(e) => setRemplacementMotif(e.target.value)}
                placeholder="Ex. ISO 15189:2022 — 4e édition, décembre 2022."
                style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                L’exemplaire restera consultable : les dossiers figés avant ce remplacement ont été
                jugés contre cette édition, et devront pouvoir le dire.
              </div>
              <PiedActions taille="sm" annuler={{ onClick: () => { setRemplacementMotif(null); setErreurExterne(''); } }} principale={{ libelle: 'Marquer comme remplacée', libelleChargement: 'Enregistrement…', chargement: occupeFiche('remplacer'), disabled: occupeFiche() || !remplacementMotif.trim(), onClick: () => void runFiche('remplacer', async () => {
                  const ok = await appelerParcoursExterne(
                    doc.documentId, 'remplacer', { motif: remplacementMotif.trim() }, setErreurExterne, refresh,
                  );
                  if (ok) setRemplacementMotif(null);
                }) }} />
            </div>
          )}
          {revisionMotif !== null && (
            <div style={{ ...card, padding: 14, marginTop: 12, display: 'grid', gap: 8 }}>
              <label htmlFor="rev-motif" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary-active)' }}>
                Motif de la révision (facultatif)
              </label>
              <textarea id="rev-motif" autoFocus rows={2} value={revisionMotif} onChange={(e) => setRevisionMotif(e.target.value)}
                placeholder="Ex. évolution de la technique de prélèvement — mise à jour du §4."
                style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                La v{doc.versionCourante} reste applicable jusqu’à la mise en vigueur de la nouvelle version.
              </div>
              <PiedActions taille="sm" annuler={{ onClick: () => setRevisionMotif(null) }} principale={{ libelle: 'Déclencher la révision', libelleChargement: 'Ouverture…', chargement: occupeFiche('revision'), disabled: occupeFiche(), onClick: () => void runFiche('revision', async () => {
                  const motif = revisionMotif.trim() || undefined;
                  const ok = await act(doc.documentId, 'revision', { motif });
                  if (ok) setRevisionMotif(null);
                }) }} />
            </div>
          )}
          {urgenceMotif !== null && (
            <div style={{ ...card, padding: 14, marginTop: 12, display: 'grid', gap: 8, borderColor: 'var(--red-100)' }}>
              <label htmlFor="urgence-motif" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--red-600)' }}>
                Motif du retrait d’urgence (obligatoire)
              </label>
              <textarea id="urgence-motif" autoFocus rows={2} value={urgenceMotif} onChange={(e) => setUrgenceMotif(e.target.value)}
                placeholder="Ex. erreur critique détectée au §4 — risque patient."
                style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                Le document sort immédiatement du service et ses {(doc.diffusion || []).length} destinataire(s) sont
                notifiés « ne pas appliquer ». Il ne reviendra en vigueur qu’en repassant par le circuit complet —
                à distinguer du gel, qui suspend l’évolution d’un document qui reste applicable.
              </div>
              <PiedActions taille="sm" annuler={{ onClick: () => setUrgenceMotif(null) }} principale={{ libelle: 'Retirer & notifier', libelleChargement: 'Retrait en cours…', chargement: occupeFiche('retrait'), variante: 'danger', disabled: occupeFiche() || !urgenceMotif.trim(), onClick: () => void runFiche('retrait', async () => {
                  const ok = await act(doc.documentId, 'retrait', { motif: urgenceMotif.trim() });
                  if (ok) setUrgenceMotif(null);
                }) }} />
            </div>
          )}
          {/* Gel de l'applicabilité (GED-5). Le panneau dit ce que le gel FAIT et
              ce qu'il ne fait pas : c'est la confusion avec le retrait d'urgence
              qu'il faut empêcher au moment du clic, pas après. */}
          {gelMotif !== null && (
            <div style={{ ...card, padding: 14, marginTop: 12, display: 'grid', gap: 8 }}>
              <label htmlFor="gel-motif" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary-active)' }}>
                Motif du gel (obligatoire)
              </label>
              <textarea id="gel-motif" autoFocus rows={2} value={gelMotif} onChange={(e) => setGelMotif(e.target.value)}
                placeholder="Ex. avis attendu du fournisseur sur la trousse — aucune évolution du document d’ici là."
                style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
              <label htmlFor="gel-jusqu" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
                Jusqu’au (facultatif)
              </label>
              <input id="gel-jusqu" type="date" value={gelJusqu} onChange={(e) => setGelJusqu(e.target.value)} style={inputStyle} />
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                La v{doc.versionCourante} reste applicable, lisible et imprimable : le gel suspend l’ÉVOLUTION du document
                (plus de révision, de rédaction ni d’approbation) — ce n’est pas un retrait d’urgence.
                Les {(doc.diffusion || []).length} destinataire(s) de sa diffusion sont invités à en prendre connaissance.
              </div>
              <PiedActions taille="sm" annuler={{ onClick: () => setGelMotif(null) }} principale={{ libelle: 'Geler l’applicabilité', libelleChargement: 'Gel en cours…', chargement: occupeFiche('gel'), disabled: occupeFiche() || !gelMotif.trim(), onClick: () => void runFiche('gel', async () => {
                  const ok = await act(doc.documentId, 'gel', { motif: gelMotif.trim(), jusqu: gelJusqu || undefined });
                  if (ok) setGelMotif(null);
                }) }} />
            </div>
          )}
          {degelMotif !== null && (
            <div style={{ ...card, padding: 14, marginTop: 12, display: 'grid', gap: 8 }}>
              <label htmlFor="degel-motif" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary-active)' }}>
                Motif de la levée (obligatoire)
              </label>
              <textarea id="degel-motif" autoFocus rows={2} value={degelMotif} onChange={(e) => setDegelMotif(e.target.value)}
                placeholder="Ex. avis du fournisseur reçu le 12/09 — la révision peut reprendre."
                style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                Le document reprend son cycle normal. La levée est inscrite au journal signé, comme le gel.
              </div>
              <PiedActions taille="sm" annuler={{ onClick: () => setDegelMotif(null) }} principale={{ libelle: 'Lever le gel', libelleChargement: 'Levée en cours…', chargement: occupeFiche('degel'), disabled: occupeFiche() || !degelMotif.trim(), onClick: () => void runFiche('degel', async () => {
                  const ok = await act(doc.documentId, 'degel', { motif: degelMotif.trim() });
                  if (ok) setDegelMotif(null);
                }) }} />
            </div>
          )}
          <TitrePage titre={doc.titre} />
          {/* ── LES DEUX FACES DU DOCUMENT, NOMMÉES (RQ, 07/10/2026) ───────
              « Ça doit être des entités différentes. » La mention cliquable du
              06/10 disait déjà les deux versions, mais c'était une PHRASE : on
              pouvait travailler sans la voir, et rien ne rappelait ensuite sur
              quelle face on se trouvait. La bascule, elle, est permanente et
              porte le nom de chaque face.

              Un `Segment` et non deux boutons : choisir une vue n'est pas une
              action (conventions d'interface §1), et un bouton plein ici
              concurrencerait « Terminer la rédaction ».

              Masquée quand on lit une ARCHIVE : la fiche ne montre alors
              aucune des deux faces, et une bascule à deux options mentirait sur
              ce qui est affiché — le bandeau gris et son lien de retour
              gouvernent ce cas. */}
          {revision && !litUneArchive && (
            <div style={{ marginTop: 10 }}>
              <Segment
                ariaLabel="Version du document à consulter"
                valeur={faceCourante}
                onChange={(v) => {
                  choisirVersion(null);
                  if (v === 'chantier') {
                    setVoirEnCours(true);
                    /* En rédaction il n'existe pas d'aperçu officiel du
                       brouillon : le texte se travaille dans le fichier. */
                    setDocTab(etapeCand === 'redaction' ? 'fichier' : 'lecture');
                  } else {
                    setVoirEnCours(false);
                    setDocTab('lecture');
                  }
                }}
                options={[
                  { valeur: 'applicable' as Registre, libelle: `Version applicable (v${numApplicable})`,
                    aide: 'Ce que le laboratoire applique aujourd’hui — c’est de cette version qu’on accuse réception.' },
                  { valeur: 'chantier' as Registre, libelle: `Révision en cours (v${numCandidat})`,
                    disabled: !accesEnCours.ouvert,
                    aide: accesEnCours.ouvert
                      ? `La nouvelle version, ${libelleEtape(etapeCand)} — elle n’est pas applicable.`
                      : accesEnCours.refus },
                ]}
              />
            </div>
          )}
          {banner && (
            <div style={{ marginTop: 10, background: banner.bg, color: banner.fg, borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span>{banner.text}</span>
              {'lien' in banner ? banner.lien : null}
            </div>
          )}
        </div>

        {/* L'aperçu passe en pleine largeur et les métadonnées sous lui. Le
            rail de 280px à droite rognait la page du document — or c'est LE
            contenu qu'on vient lire ; le type, la discipline ou le nom de
            l'approbateur se consultent après coup, et une fois. */}
        <div style={{ display: 'grid', gap: 20 }}>
          {/* L'aide à la trame a REJOINT l'onglet « Fichier » (18/08). Elle
              vivait ici, au-dessus des onglets — donc au-dessus de l'aperçu, du
              circuit et des commentaires, alors qu'elle ne sert qu'à une chose :
              écrire dans le fichier. Depuis qu'elle y insère directement le plan,
              sa place est là où se trouve ce qu'elle modifie. */}
          <div style={{
            ...card, overflow: 'hidden',
            /* LE CADRE. Il borde tout ce qu'on lit — onglets, aperçu, fichier —
               et ne disparaît pas au défilement : c'est lui qui répond, sans
               qu'on ait à relire une phrase, à « sur quoi suis-je ? ». */
            ...(faceNonApplicable ? { border: `2px solid ${faceNonApplicable.teinte}` } : null),
          }}>
            {faceNonApplicable && (
              <div role="note" style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '9px 16px', background: faceNonApplicable.fond,
                color: faceNonApplicable.teinte, fontSize: 12.5, fontWeight: 700,
                letterSpacing: '0.02em',
              }}>
                <I n="alert" s={14} />{faceNonApplicable.mot}
              </div>
            )}
            <div style={{ display: 'flex', gap: 2, padding: '0 14px', borderBottom: '1px solid var(--border-default)', overflowX: 'auto' }}>
              {tabs.map((t) => (
                <button key={t.key} onClick={() => setDocTab(t.key)} className="ged-btn" style={{ fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600, background: 'none', border: 'none', borderBottom: `2px solid ${docTab === t.key ? 'var(--color-primary)' : 'transparent'}`, color: docTab === t.key ? 'var(--color-primary-active)' : 'var(--text-body)', padding: '12px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* L'onglet de lecture montre désormais l'aperçu PDF officiel — le
                document tel qu'il s'imprime, cartouche compris — et non plus le
                seul contenu structuré, qui était vide dès que le contenu réel
                vivait dans le fichier bureautique. */}
            {docTab === 'lecture' && <ApercuTab doc={doc} version={versionVue || undefined} />}

            {docTab === 'fichier' && <><GestionApercu doc={doc} /><OfficeTab doc={doc} /></>}

            {docTab === 'versions' && (
              <div>
                {/* Ce que cet onglet sait faire depuis le 06/10/2026, et ne
                    savait pas faire avant : OUVRIR une version passée. Chaque
                    mise en vigueur archive un exemplaire du fichier, et c'est
                    lui qu'on sert — avec le cartouche de CETTE version (ses
                    signataires, sa période de vigueur) et le filigrane « ne pas
                    appliquer ». Les versions mises en vigueur avant cette date
                    n'ont pas d'exemplaire : on le DIT, au lieu d'offrir un
                    bouton qui mènerait à un message d'erreur. */}
                {(doc.versions || []).slice().reverse().map((v) => (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 20px', borderTop: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 700, color: 'var(--navy-900)', minWidth: 48 }}>v{v.num}</span>
                    <span style={{ fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: v.obsolete ? 'var(--gray-50)' : v.dateVigueur ? 'var(--green-50)' : 'var(--cyan-50)', color: v.obsolete ? 'var(--text-muted)' : v.dateVigueur ? 'var(--green-600)' : 'var(--cyan-700)' }}>
                      {v.obsolete ? 'Obsolète' : v.dateVigueur ? 'En vigueur' : v.brouillon ? 'Brouillon' : 'En circuit'}
                    </span>
                    <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-muted)' }}>
                      Réd. {userName(settings, v.redacteur || '')} {v.verificateur ? `· Vér. ${userName(settings, v.verificateur)}` : ''} {v.approbateur ? `· App. ${userName(settings, v.approbateur)}` : ''}
                    </span>
                    {/* Version du catalogue référencée depuis le manuel : le PDF figé est
                        servi par la route livret du manuel, qui applique les droits
                        (personnel : tout ; confrère : version en vigueur seule). */}
                    {/* `archivees === null` : la réponse n'est pas arrivée — on ne
                        propose rien et on n'affirme rien (faux vides). */}
                    {archivees !== null && !v.brouillon && (() => {
                      const dispo = archivees.includes(String(v.num));
                      // Déjà la version que l'onglet Aperçu montre : un second
                      // bouton vers le même écran n'apprend rien — on dit juste
                      // laquelle des lignes on est en train de lire.
                      if (String(v.num) === versionVue) {
                        return <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>affichée dans l’Aperçu</span>;
                      }
                      return dispo ? (
                        <Bouton taille="sm" variante="contour"
                          title={`Ouvrir l’exemplaire archivé de la v${v.num} — cartouche de cette version, filigrane « ne pas appliquer ».`}
                          onClick={() => { choisirVersion(String(v.num)); setVoirEnCours(false); setDocTab('lecture'); }}>
                          <I n="doc" s={13} />Consulter
                        </Bouton>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}
                          title="Les exemplaires de version ne sont archivés que depuis le 06/10/2026 : le fichier de travail a depuis été réécrit par les versions suivantes.">
                          pas d’exemplaire archivé
                        </span>
                      );
                    })()}
                    {v.livret && (
                      <a href={`${MANUEL_URL}/api/livret/${v.livret}`} target="_blank" rel="noreferrer"
                        title="Consulter le livret PDF figé de cette version (servi par le manuel, droits appliqués)"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)', background: 'var(--cyan-50)', border: '1px solid var(--cyan-200)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', textDecoration: 'none' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" /><path d="M14 2v6h6" /></svg>
                        Consulter le PDF
                      </a>
                    )}
                    {/* Ce qui a changé par rapport à la version d'avant (22/10/2026) —
                        pas de bouton sur la première version, il n'y a rien à comparer. */}
                    {!v.brouillon && (doc.versions || []).findIndex((x) => x.id === v.id) > 0 && (
                      <Bouton taille="sm" variante="contour" aria-expanded={changementsOuverts === String(v.num)}
                        title={`Voir ce que la v${v.num} change par rapport à la version précédente, sans relire le document.`}
                        onClick={() => setChangementsOuverts(changementsOuverts === String(v.num) ? null : String(v.num))}>
                        <I n="doc" s={13} />Ce qui change
                      </Bouton>
                    )}
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-body)' }}>{v.dateVigueur ? fmtDate(v.dateVigueur) : '—'}</span>
                    {changementsOuverts === String(v.num) && (
                      <div style={{ flexBasis: '100%' }}>
                        <ChangementsVersion documentId={doc.documentId} a={String(v.num)} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* PLUS D'ONGLET « JOURNAL » (RQ, 24/08/2026). Le document l'avait
               bien avant les autres registres, mais le bouton dédié montre la
               même chose et existe partout : garder l'onglet ici seul aurait
               laissé deux façons d'ouvrir un journal dans l'application. */}

            {docTab === 'diffusion' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '14px 20px 4px' }}>
                  <p style={{ fontSize: 12.5, color: 'var(--text-body)', fontWeight: 600, margin: 0, flex: 1, minWidth: 200 }}>
                    Liste de diffusion — v{doc.versionCourante} · {ackSummary}
                    {relancesTotal > 0 && <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}> · {relancesTotal} relance(s) déjà tracée(s)</span>}
                  </p>
                  {roles.includes('rq') && attenteAck > 0 && (
                    /* Le bouton reste immobilisé jusqu'au retour du serveur.
                       Depuis GED-4 b la route se garde elle-même contre le
                       double clic (délai minimal entre deux relances d'une même
                       personne), mais un bouton qui reste cliquable pendant un
                       envoi de courriels laisse croire qu'il ne s'est rien passé. */
                    <Bouton taille="sm" onClick={() => void runFiche('relance', () => act(doc.documentId, 'relance'))} disabled={occupeFiche()} title="Envoie un courriel de rappel à chaque lecteur qui doit encore accuser réception, et inscrit au journal ce qui est réellement parti (remis, en échec, sans adresse). Les personnes « pour information » ne sont pas relancées : elles n’ont aucun accusé à donner.">
                      <I n="mail" s={13} />{occupeFiche('relance') ? 'Relance…' : `Relancer (${attenteAck})`}
                    </Bouton>
                  )}
                </div>
                {(doc.diffusion || []).map((x, i) => (
                  <div key={`${x.userId}-${i}`} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 20px' }}>
                    <span style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--cyan-100)', color: 'var(--cyan-700)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, flex: 'none' }}>{userInitials(settings, x.userId)}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)' }}>{userName(settings, x.userId)}</span>
                      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                        {x.requis ? 'Accusé requis' : 'Pour information'}
                        {x.dateOuverture ? ` · ouvert le ${fmtDate(x.dateOuverture)}` : ' · jamais ouvert'}
                      </span>
                    </span>
                    {x.dateAck ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--green-600)' }}><I n="check" s={14} />Lu le {fmtDate(x.dateAck)}</span>
                    ) : (
                      // L'échéance était invisible : « en attente » ne disait pas
                      // depuis quand, ni jusqu'à quand. Le retard se voit ici,
                      // sur la ligne de la personne concernée.
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, color: x.echeance && jourIso(x.echeance) < aujourdhui ? 'var(--red-600)' : 'var(--amber-600)' }}>
                        <I n="clock" s={14} />
                        {x.echeance
                          ? (jourIso(x.echeance) < aujourdhui ? `En retard depuis le ${fmtDate(x.echeance)}` : `À lire avant le ${fmtDate(x.echeance)}`)
                          : 'En attente'}
                        {x.relances ? ` · ${x.relances} relance(s)` : ''}
                      </span>
                    )}
                    {/* Le geste de retrait n'est offert qu'au RQ — c'est le même
                        droit que celui d'ajouter un destinataire. Sur une ligne
                        déjà accusée, le libellé change : on ne propose JAMAIS
                        « retirer » sur une preuve de lecture, on propose ce que
                        le serveur acceptera. */}
                    {roles.includes('rq') && (
                      <Bouton taille="sm" onClick={() => { setRetraitCible(retraitCible === x.userId ? null : x.userId); setRetraitMotif(''); }} disabled={occupeFiche()} title={x.dateAck
                          ? 'Cette personne a accusé réception : son accusé ne s’efface pas. On peut en revanche cesser de lui demander un accusé pour l’avenir.'
                          : 'Retirer cette personne de la liste de diffusion. Un motif est obligatoire, et le retrait est inscrit au journal signé du document.'} style={{ flex: 'none' }}>
                        <I n="x" s={13} />{x.dateAck ? 'Ne plus exiger' : 'Retirer'}
                      </Bouton>
                    )}
                    </div>
                    {retraitCible === x.userId && (
                      <div style={{ padding: '0 20px 14px 64px', display: 'grid', gap: 8 }}>
                        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                          {x.dateAck ? (
                            <>
                              <strong>{userName(settings, x.userId)} a accusé réception le {fmtDate(x.dateAck)}.</strong> L’accusé est une preuve : il ne s’efface pas,
                              et la ligne ne peut pas être retirée. Ce qui reste possible, c’est de ne plus lui demander d’accusé <strong>pour l’avenir</strong> —
                              l’accusé déjà donné et sa trace au journal restent intacts.
                            </>
                          ) : (
                            <>Retirer {userName(settings, x.userId)} de la diffusion, c’est décider qu’il ou elle n’avait pas à lire ce document.
                              Le motif figurera au journal signé, à côté de l’événement de diffusion qui a créé cette ligne.</>
                          )}
                        </div>
                        <label htmlFor={`retrait-motif-${i}`} style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary-active)' }}>
                          Motif (obligatoire)
                        </label>
                        <textarea id={`retrait-motif-${i}`} autoFocus rows={2} value={retraitMotif} onChange={(e) => setRetraitMotif(e.target.value)}
                          placeholder={x.dateAck
                            ? 'Ex. a changé de poste — ce document ne relève plus de son activité.'
                            : 'Ex. diffusé par erreur — ne travaille pas sur ce secteur.'}
                          style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <Bouton variante="primaire" taille="sm" onClick={() => void runFiche('retrait-dest', async () => {
                            const ok = await act(doc.documentId, 'destinataire/retrait', {
                              userId: x.userId, motif: retraitMotif.trim(), pourAvenir: !!x.dateAck,
                            });
                            if (ok) { setRetraitCible(null); setRetraitMotif(''); }
                          })} disabled={occupeFiche() || !retraitMotif.trim()}>
                            <I n="check" s={13} />
                            {occupeFiche('retrait-dest') ? 'En cours…' : (x.dateAck ? 'Ne plus demander d’accusé' : 'Retirer de la diffusion')}
                          </Bouton>
                          <Bouton taille="sm" onClick={() => { setRetraitCible(null); setRetraitMotif(''); }}>Annuler</Bouton>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {(doc.diffusion || []).length === 0 && <div style={{ padding: '24px 20px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>Aucune diffusion émise pour ce document.</div>}
                {/* La RÈGLE d'audience avant l'ajout manuel : c'est elle qui
                    porte l'intention du rédacteur, et c'est par elle qu'on
                    diffuse. Le choix nominatif à l'émission reste possible —
                    il n'est plus le seul chemin. */}
                <AudiencePanel doc={doc} />
                {roles.includes('rq') && <DiffusionAdder doc={doc} />}
              </div>
            )}

            {docTab === 'liens' && (
              <div style={{ padding: '14px 20px 18px' }}>
                <Liens type="document" refCourante={doc.code} />
              </div>
            )}

            {docTab === 'comments' && (
              <div>
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', padding: '12px 20px 4px' }}>Commentaires internes qualité — jamais imprimés, jamais diffusés.</p>
                {(doc.commentaires || []).map((c) => (
                  <div key={c.id} style={{ display: 'flex', gap: 12, padding: '12px 20px', borderTop: '1px solid var(--border-subtle)' }}>
                    <span style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--surface-sunken)', color: 'var(--text-body)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 700, flex: 'none' }}>{userInitials(settings, c.auteur)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5 }}><strong style={{ color: 'var(--text-strong)' }}>{c.auteurName}</strong> <span style={{ color: 'var(--text-muted)' }}>· {fmtDateTime(c.ts)} · v{c.version}</span></div>
                      <p style={{ fontSize: 13, color: 'var(--text-body)', margin: '4px 0 0', lineHeight: 1.5 }}>{c.texte}</p>
                    </div>
                    {(c.auteur === session.key || session.roles.includes('rq')) && (
                      // La suppression partait sans confirmation, sans `.catch` et
                      // sans message : un refus serveur laissait la ligne à
                      // l'écran, et l'utilisateur concluait à un bug.
                      <ConfirmButton
                        label="" icon="trash" variant="danger" titre="Supprimer ce commentaire"
                        question="Supprimer ce commentaire ? Il ne sera pas récupérable."
                        confirmer="Supprimer"
                        onConfirm={async () => {
                          const { showToast, refresh } = useGed.getState();
                          try {
                            const r = await fetchEcriture(`/api/ged/ged/docs/${doc.documentId}/comment/${c.id}`, { method: 'DELETE', credentials: 'include' });
                            if (!r.ok) { showToast(r.status === 403 ? 'Vous n’avez pas le droit de supprimer ce commentaire.' : 'La suppression a échoué.', 'erreur'); return; }
                            await refresh();
                            showToast('Commentaire supprimé.');
                          } catch {
                            showToast('Serveur injoignable — le commentaire n’a pas été supprimé.', 'erreur');
                          }
                        }} />
                    )}
                  </div>
                ))}
                {(doc.commentaires || []).length === 0 && <div style={{ padding: '20px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>Aucun commentaire.</div>}
                {/* Un commentaire qualité est un texte de fond : `<input>` monoligne
                    coupait la rédaction, et il fallait viser le bouton à chaque
                    message (Ctrl+Entrée publie). */}
                <div style={{ padding: '14px 20px', borderTop: '1px solid var(--border-default)', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <textarea value={commentDraft} onChange={(e) => setCommentDraft(e.target.value)} rows={2}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey)) return;
                        e.preventDefault();
                        if (!commentDraft.trim()) return;
                        void runFiche('comment', async () => {
                          const ok = await act(doc.documentId, 'comment', { texte: commentDraft.trim() });
                          if (ok) setCommentDraft('');
                        });
                      }}
                      placeholder={`Ajouter un commentaire — en tant que ${session.name}`}
                      style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>Ctrl + Entrée pour publier.</div>
                  </div>
                  <Bouton variante="primaire" onClick={() => void runFiche('comment', async () => {
                    const ok = await act(doc.documentId, 'comment', { texte: commentDraft.trim() });
                    if (ok) setCommentDraft('');
                  })} disabled={!commentDraft.trim() || occupeFiche()}>
                    <I n="send" s={14} />{occupeFiche('comment') ? 'Publication…' : 'Publier'}
                  </Bouton>
                </div>
              </div>
            )}
          </div>

          {/* ── Parcours du DOCUMENT EXTERNE (14/08/2026) ───────────────────
              Enregistré → adopté → remplacé, à la place de vérifier/approuver :
              le laboratoire n'a pas écrit ce texte, il déclare s'y référer.
              Posé dans la FICHE et non dans l'écran de revue — la revue est le
              circuit des documents qu'on rédige, et un document externe n'y
              passe jamais. */}
          <ParcoursExterne doc={doc} />

          {/* Bandeau méta — sous l'aperçu, en colonnes qui se replient seules
              selon la largeur disponible. */}
          <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', alignItems: 'start' }}>
            <div style={{ ...card, padding: '16px 18px', gridColumn: '1 / -1' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <h3 style={{ margin: 0, fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Métadonnées</h3>
                <span style={{ flex: 1 }} />
                {/* La route de correction existait depuis longtemps, réservée au
                    RQ et journalisée — mais AUCUN écran ne l'appelait : seul un
                    appel direct à l'API pouvait l'atteindre. Le RQ constatait
                    donc une erreur de titre ou de processus sans aucun moyen de
                    la corriger lui-même. */}
                {roles.includes('rq') && STATUTS_AVANT_DIFFUSION.includes(doc.statut) && (
                  <Bouton taille="sm" onClick={() => setCorrigeMeta(true)} title="Corriger les rubriques descriptives. Motif obligatoire, inscrit au journal signé avec l’avant/après.">
                    <I n="pen" s={12} />Modifier les métadonnées
                  </Bouton>
                )}
                {roles.includes('rq') && doc.statut === 'envigueur' && (
                  <Bouton taille="sm" onClick={() => setCorrigeIntitule(true)} title="Corriger l’intitulé du document. Motif obligatoire, inscrit au journal signé avec l’avant/après.">
                    <I n="pen" s={12} />Corriger l’intitulé
                  </Bouton>
                )}
              </div>
              {/* Un document EN VIGUEUR dont aucun exemplaire n'est déposé
                  affirme quelque chose de faux : il n'y a rien à appliquer.
                  Le dire ici plutôt que de laisser la pastille verte rassurer. */}
              {doc.statut === 'envigueur' && doc.classe === 'importe' && !doc.fichier && (
                <div role="alert" style={{ marginBottom: 10, padding: '9px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--amber-50)', color: 'var(--amber-700)', fontSize: 12.5, lineHeight: 1.5 }}>
                  Ce document externe est déclaré <strong>en vigueur</strong> alors qu’aucun exemplaire
                  n’est déposé — il n’y a donc rien à consulter ni à appliquer. Déposez le fichier
                  dans l’onglet « Fichier », ou ramenez la fiche en brouillon.
                </div>
              )}
              {/* ── CES RUBRIQUES SONT CELLES DE LA VERSION LUE ───────────────
                  Trois situations, trois phrases — et jamais de silence :
                   · on lit le CHANTIER : ses rubriques ne s'appliquent pas
                     encore, elles entreront en vigueur à l'approbation ;
                   · on lit une ARCHIVE : ce sont les rubriques de l'époque ;
                   · la version n'a pas de fiche archivée (avant le 10/10/2026) :
                     on affiche celles du document et on le DIT, plutôt que de
                     faire passer le présent pour le passé. */}
              {versionVue && (litLeChantier || litUneArchive) && (
                <div style={{ marginBottom: 10, padding: '9px 12px', borderRadius: 'var(--radius-sm)', background: litLeChantier ? 'var(--cyan-50)' : 'var(--surface-sunken)', color: 'var(--text-body)', fontSize: 12.5, lineHeight: 1.5 }}>
                  {!ficheArchivee(versionObjVue) ? PHRASE_FICHE_ABSENTE : litLeChantier ? (
                    <>Rubriques de la <strong>v{versionVue}</strong>, en cours de révision : elles remplaceront celles de la v{numApplicable} à l’approbation.</>
                  ) : (
                    <>Rubriques de la <strong>v{versionVue}</strong>, telles qu’elles étaient quand cette version faisait foi.</>
                  )}
                  {ficheArchivee(versionObjVue) && ecartsVue.length > 0 && (
                    <div style={{ marginTop: 4, color: 'var(--text-muted)' }}>
                      Différent de ce qui s’applique aujourd’hui : {ecartsVue.map((e) => LIBELLE_CHAMP[e.champ]).join(', ')}.
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '10px 22px' }}>
              {[
                ['Type', settings?.docTypes?.find((t) => t.code === String(ficheVue.docType || ''))?.label || renduValeur(ficheVue.docType)],
                // Les deux axes sont montrés SÉPARÉMENT, et un axe absent est
                // écrit « — » : le vide est ici un fait (une procédure d'achats
                // ne relève d'aucune famille biologique), pas un oubli de saisie.
                ['Processus', renduValeur(ficheVue.processus)],
                ['Discipline (famille bio.)', renduValeur(ficheVue.discipline)],
                ['Périmètre', renduValeur(ficheVue.perimetre)],
                ['Site', renduValeur(ficheVue.site)],
                ['Périodicité', renduValeur(ficheVue.periodicite)],
                // La date de vigueur est celle DE LA VERSION LUE : celle du
                // document est celle de la version applicable, et l'écrire sur
                // une archive daterait la v1.0 du jour où la v2.0 est entrée.
                ['En vigueur depuis', versionObjVue?.dateVigueur ? fmtDate(versionObjVue.dateVigueur) : (litLeChantier ? '— pas encore applicable' : fmtDate(doc.dateVigueur))],
                /* La prochaine revue ne vaut que pour ce qui s'applique : sur une
                   archive, elle a été remplacée par celle de la version suivante ;
                   sur un chantier, elle sera calculée à l'approbation. */
                ['Prochaine revue', litLeChantier ? '— fixée à l’approbation' : litUneArchive ? '—' : fmtDate(doc.prochaineRevue)],
                /* ── UN DOCUMENT EXTERNE N'A NI RÉDACTEUR NI VÉRIFICATEUR ────
                   Signalé par la RQ le 14/08/2026 sur un document externe :
                   « pourquoi on me demande un rédacteur et un vérif ? ».
                   Parce que la fiche affichait les trois rubriques du circuit
                   interne pour TOUS les types. Or ce texte n'a pas été rédigé
                   ici et personne au laboratoire ne l'a vérifié : afficher
                   « Rédacteur — » invite à remplir une case qui ne doit pas
                   l'être, et remplie, elle inscrirait au dossier une revue qui
                   n'a jamais eu lieu.

                   À la place, ce qui se demande vraiment d'un exemplaire
                   externe : QUI l'a enregistré, et QUI l'a adopté. */
                ...(externe
                  ? [
                    ['Enregistré par', userName(settings, doc.redacteur) || '—'],
                    ['Adopté par', doc.statut === 'adopte' || doc.statut === 'remplace'
                      ? (userName(settings, doc.approbateur) || '—') : '— pas encore adopté'],
                  ]
                  : [
                    /* Les intervenants DE LA VERSION LUE. Une version signée a
                       figé les siens : c'est ce qui rend vrai « une fois une
                       version approuvée, plus aucun champ du circuit ne change »
                       (RQ, 10/10/2026). Le chantier, lui, montre les désignés. */
                    [(circuitVue.redacteurs || affectes).length > 1 ? 'Rédacteurs' : 'Rédacteur',
                      (circuitVue.redacteurs || affectes).map((k) => userName(settings, k)).join(', ')],
                    ['Vérificateur', userName(settings, circuitVue.verificateur ?? doc.verificateur)],
                    ['Approbateur', userName(settings, circuitVue.approbateur ?? doc.approbateur)],
                  ]),
              ].map(([k, v]) => (
                // Libellé au-dessus de la valeur : en bandeau large, la paire
                // alignée aux deux bords laissait un vide que l'œil devait
                // franchir pour rattacher la valeur à son étiquette.
                <div key={k} style={{ fontSize: 12.5, minWidth: 0 }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{k}</div>
                  <div style={{ color: 'var(--text-strong)', fontWeight: 600, overflowWrap: 'anywhere' }}>{v || '—'}</div>
                </div>
              ))}
              </div>
              {/* ── LE CIRCUIT D'UNE VERSION APPROUVÉE NE SE MODIFIE PLUS ────
                  Demande RQ du 10/10/2026 : « une fois une version approuvée,
                  plus aucun champ du circuit ne peut être modifié (rédacteur,
                  vérificateur…) ». Les signataires d'une version signée sont
                  figés sur la version elle-même (`circuitVue`) — ce qui se
                  désigne pendant une révision, ce sont ceux de la version EN
                  CHANTIER. Laisser le bouton sous les noms de la v1.0 affichée
                  laissait croire qu'on modifiait sa signature. */}
              {roles.includes('rq') && !externe && STATUTS_AVANT_DIFFUSION.includes(doc.statut) && (
                versionSignee ? (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-subtle)', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                    Les intervenants de la v{versionVue} sont figés depuis son approbation — une signature ne se réécrit pas.
                    {!!numCandidat && <> Pour désigner ceux de la v{numCandidat}, affichez la version en cours.</>}
                  </div>
                ) : <IntervenantsManager doc={doc} affectes={affectes} version={numCandidat} />
              )}
            </div>
            {(ficheVue.referencesNorm as string[] || []).length > 0 && (
              <div style={{ ...card, padding: '16px 18px' }}>
                <h3 style={{ margin: '0 0 10px', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Références normatives</h3>
                {((ficheVue.referencesNorm as string[]) || []).map((r) => <div key={r} style={{ fontSize: 12.5, color: 'var(--text-body)', padding: '3px 0' }}>{r}</div>)}
              </div>
            )}
            {(ficheVue.codesClasseur as string[] || []).length > 0 && (
              <div style={{ ...card, padding: '16px 18px' }}>
                <h3 style={{ margin: '0 0 4px', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Origine au classeur</h3>
                {/* Ces cotes vivaient dans les mots-clés jusqu'au 14/08/2026, où
                    elles polluaient la recherche et masquaient le fait que ces
                    documents n'avaient, en réalité, aucun mot-clé. Elles ne
                    servent pas à chercher : elles servent à retrouver
                    l'original le jour où un évaluateur le demande. */}
                <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Référence de ce document dans le classeur papier ou dans le système documentaire antérieur.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {((ficheVue.codesClasseur as string[]) || []).map((c) => (
                    <span key={c} style={{ fontFamily: 'var(--font-mono)', fontSize: 11, background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '3px 8px', color: 'var(--text-body)' }}>{c}</span>
                  ))}
                </div>
              </div>
            )}
            {(ficheVue.motsCles as string[] || []).length > 0 && (
              <div style={{ ...card, padding: '16px 18px' }}>
                <h3 style={{ margin: '0 0 10px', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Mots-clés</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {((ficheVue.motsCles as string[]) || []).map((k) => <span key={k} style={{ fontSize: 12.5, background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-pill)', padding: '3px 10px', color: 'var(--text-body)' }}>{k}</span>)}
                </div>
              </div>
            )}
            <CopiesMaitrisees doc={doc} version={versionVue} />
            {/* Les étiquettes ne sont PAS versionnées (décision RQ du
                11/10/2026) : elles servent à retrouver un document, et se
                posent ou se retirent à tout moment — y compris pendant une
                révision, où figer le champ interdisait de reclasser ce qui
                s'applique. Elles vivent sur le document. */}
            <TagsEditor doc={doc} />
          </div>
        </div>
      </div>
      {/* Correction des rubriques descriptives — RQ, motivée, journalisée. */}
      {corrigeMeta && <CorrigerMetadonnees doc={doc} onFerme={() => setCorrigeMeta(false)} />}
      {corrigeIntitule && <CorrigerIntitule doc={doc} onFerme={() => setCorrigeIntitule(false)} />}
    </div>
  );
}

/* ------------------------------ Éditeur ------------------------------ */
export function Review() {
  const { go, act, session, settings, events, loadJournal, refresh } = useGed();
  const doc = useSelectedDoc();
  const [comment, setComment] = useState('');
  const [motifDerogation, setMotifDerogation] = useState('');
  const [erreurRelecture, setErreurRelecture] = useState('');
  const [declarationOuverte, setDeclarationOuverte] = useState(false);
  // Vérifier, approuver et renvoyer sont des transitions SIGNÉES : un double
  // clic pendant l'appel réseau les rejouait. Le garde-fou manquait ici alors
  // qu'il a été posé sur les transitions des autres registres.
  const { run: runRevue, estOccupe: occupeRevue } = useBusy();
  useEffect(() => { if (doc) void loadJournal(doc.code); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [doc?.code]);

  if (!doc) return null;
  const cand = (doc.versions || []).find((v) => !v.dateVigueur && !v.obsolete);
  const journal = (events[doc.code] || []).filter((e) => e.signature);
  const isVerify = doc.statut === 'averifier';
  const isApprove = doc.statut === 'aapprouver';
  // Séparation des tâches ISO 15189 §8.3, MÊME base que le serveur
  // (`redacteursDe`) : contributeurs de la version + rédacteur de la version +
  // rédacteurs affectés. Sans cette garde côté écran, le bouton « Signer la
  // vérification » s'affichait puis le serveur refusait — l'utilisateur ne
  // comprenait pas pourquoi son document restait bloqué.
  const redacteursDe = Array.from(new Set([
    ...(cand?.contributeurs || []),
    cand?.redacteur,
    doc.redacteur,
    ...(doc.redacteurs || []),
  ].filter(Boolean) as string[]));
  const aRedige = redacteursDe.includes(session.key);
  const canVerify = isVerify && !aRedige && (session.roles.includes('verificateur') || session.roles.includes('rq'));
  // DÉROGATION DU RQ : approuver un document qu'on a rédigé, avec motif, si une
  // autre personne l'a vérifié (même règle que le moteur — `peutApprouverSonTexte`).
  const derogationRedacteur = isApprove && peutApprouverSonTexte(doc, session);
  const canApprove = derogationRedacteur || (isApprove && !aRedige && doc.verificateur !== session.key
    && (session.roles.includes('approbateur') || session.roles.includes('rq')));
  const stage = isVerify ? 'Vérification' : 'Approbation';
  /* RELECTURE OBLIGATOIRE (03/10/2026, `backend/src/relecture.ts`) : on ne
     signe pas un document qu'on n'a pas ouvert en révision. Tant que la trace
     n'existe pas, le document est derrière un bouton « Ouvrir en révision » et
     la signature est grisée — le moteur la refuse de toute façon. */
  const etape = isVerify ? 'verification' : 'approbation';
  const peutSigner = canVerify || canApprove;
  const relu = (cand?.relectures || []).some((r) => r.par === session.key && r.etape === etape);
  const ouvrirEnRevision = () => runRevue('relecture', async () => {
    setErreurRelecture('');
    const r = await fetchEcriture(`/api/ged/ged/docs/${doc.documentId}/relecture`, { method: 'POST', credentials: 'include' });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      setErreurRelecture(j?.error?.message || 'Ouverture en révision refusée.');
      return;
    }
    await refresh();
  });
  const motifRenvoi = comment.trim();
  const boutonRenvoi = (
    <>
      {/* À l'approbation, on peut aussi RENDRE LE DOCUMENT AU VÉRIFICATEUR (RQ,
          03/10/2026) : revérifier un point sans tout reprendre à la rédaction. */}
      {isApprove && !!doc.verificateur && (
        <Bouton onClick={() => void runRevue('returnVerif', async () => { const ok = await act(doc.documentId, 'return-verification', { commentaire: motifRenvoi }); if (ok) go('doc'); })} disabled={occupeRevue() || !motifRenvoi} title={`Le document repasse « à vérifier » chez ${userName(settings, doc.verificateur)}, qui devra le revérifier et le signer de nouveau.`}>
          <I n="back" s={14} />{occupeRevue('returnVerif') ? 'Renvoi en cours…' : `Rendre au vérificateur (${userName(settings, doc.verificateur)})`}
        </Bouton>
      )}
      <Bouton onClick={() => void runRevue('return', async () => { const ok = await act(doc.documentId, 'return', { commentaire: motifRenvoi }); if (ok) go('doc'); })} disabled={occupeRevue() || !motifRenvoi}>
        <I n="back" s={14} />{occupeRevue('return') ? 'Renvoi en cours…' : 'Renvoyer au rédacteur'}
      </Bouton>
      {!motifRenvoi && (
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Pour renvoyer, écrivez dans le commentaire ce que le destinataire doit reprendre ou revoir.
        </span>
      )}
    </>
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {declarationOuverte && (
        <ModaleVerification dejaEnregistre={((cand as { correcteurs?: string[] } | undefined)?.correcteurs || []).includes(session.key)}
          occupe={occupeRevue('verify')} onAnnuler={() => setDeclarationOuverte(false)}
          onSigner={(modifications) => void runRevue('verify', async () => {
            const ok = await act(doc.documentId, 'verify', { commentaire: comment, modifications });
            if (ok) { setDeclarationOuverte(false); go('doc'); }
          })} />
      )}
      <div>
        <button onClick={() => go('doc')} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: 'var(--font-sans)' }}>
          <I n="back" s={15} />Retour à la fiche
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 17, fontWeight: 700, color: 'var(--navy-900)' }}>{doc.code}</span>
          <BoutonJournal reference={doc.code} quoi="de ce document" />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-muted)' }}>v{cand?.num || doc.versionCourante}</span>
          <StatePill statut={doc.statut} />
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--cyan-700)', background: 'var(--cyan-50)', borderRadius: 'var(--radius-pill)', padding: '4px 12px' }}>Étape : {stage}</span>
        </div>
      </div>

      {/* CE QUI CHANGE (RQ, 22/10/2026) : le vérificateur et l'approbateur lisent d'abord
          ce qui est NOUVEAU par rapport à la version en vigueur, sans relire le
          document en entier. Rien sur une première version. */}
      {(doc.versions || []).length > 1 && (
        <ChangementsVersion documentId={doc.documentId} a={cand?.num} masquerSiPremiere />
      )}
      {/* RETOUCHES FAITES EN VÉRIFICATION (RQ, 03/11/2026) : l'éditeur ne suit plus
          les modifications. Ce que le vérificateur a changé se lit ici, contre le
          fichier tel qu'il a été soumis — pour lui pendant qu'il travaille, pour
          l'approbateur ensuite. */}
      {String((doc.fichier as { ext?: string } | null | undefined)?.ext || '').toLowerCase() === 'docx' && (canVerify || isApprove) && (
        <ChangementsVersion documentId={doc.documentId} de="soumission" actualisable={isVerify}
          titre={isVerify ? 'Vos retouches depuis la soumission' : `Retouches du vérificateur${doc.verificateur ? ` (${userName(settings, doc.verificateur)})` : ''}`} />
      )}

      <div className="ged-2col-wide" style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>
        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ padding: '10px 20px', background: 'var(--amber-50)', color: 'var(--amber-600)', fontSize: 12.5, fontWeight: 600, borderBottom: '1px solid var(--border-default)' }}>
            {!doc.fichier
              ? 'Contenu figé — lecture seule pendant la revue.'
              : isVerify
                ? 'Vérification — vous corrigez directement dans le texte ; vos retouches se lisent dans « Vos retouches depuis la soumission ». Retouches MINEURES seulement ; au-delà, renvoyez le document au rédacteur.'
                : 'Approbation — commentaires possibles, le contenu ne se modifie plus. Une correction nécessaire = renvoi au rédacteur.'}
          </div>
          {/* Document rédigé dans OnlyOffice : c'est le FICHIER qui fait foi. La
              revue n'affichait que le contenu structuré — donc une page vide
              pour tout document Word. Les droits (révision / commentaires /
              lecture) restent décidés par le serveur. */}
          {peutSigner && !relu ? (
            <div style={{ padding: 28, display: 'grid', gap: 12, justifyItems: 'center', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-body)', maxWidth: 460, lineHeight: 1.55 }}>
                Avant de {isVerify ? 'signer la vérification' : 'approuver'}, ouvrez le document en révision.
                L’ouverture est inscrite au journal ; la signature n’est possible qu’après.
              </p>
              <Bouton variante="primaire" onClick={() => void ouvrirEnRevision()} disabled={occupeRevue()}>
                <I n="pen" s={14} />{occupeRevue('relecture') ? 'Ouverture…' : 'Ouvrir le document en révision'}
              </Bouton>
              {erreurRelecture && <FieldError>{erreurRelecture}</FieldError>}
            </div>
          ) : doc.fichier
            ? <OfficeTab doc={doc} />
            : <div className="ged-read" style={{ padding: 22 }} dangerouslySetInnerHTML={{ __html: cand?.contenu || '' }} />}
        </div>

        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ ...card, padding: '16px 18px' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Circuit</h3>
            {[
              [(doc.redacteurs?.length || 0) > 1 ? 'Rédacteurs' : 'Rédacteur',
                (doc.redacteurs?.length ? doc.redacteurs : [doc.redacteur]).filter(Boolean).map((k) => userName(settings, k as string)).join(', ')],
              ['Vérificateur', userName(settings, doc.verificateur)],
              ['Approbateur', userName(settings, doc.approbateur)],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '5px 0', fontSize: 12.5 }}>
                <span style={{ color: 'var(--text-muted)' }}>{k}</span>
                <span style={{ color: 'var(--text-strong)', fontWeight: 600 }}>{v}</span>
              </div>
            ))}
            {journal.length > 0 && (
              <>
                <h3 style={{ margin: '14px 0 8px', fontSize: 12.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>Signatures scellées</h3>
                {journal.map((e) => (
                  <div key={e.chainHash} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 0', borderTop: '1px dashed var(--border-subtle)' }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{e.evType} — {e.actorName}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--color-primary-active)' }}>{e.signature}</span>
                  </div>
                ))}
              </>
            )}
          </div>

          {(canVerify || canApprove) ? (
            <div style={{ ...card, padding: '16px 18px', display: 'grid', gap: 10 }}>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Commentaire — facultatif pour signer, obligatoire pour renvoyer au rédacteur" rows={3} style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />
              {canVerify && (
                <Bouton variante="primaire" disabled={occupeRevue() || !relu} onClick={() => {
                  /* Les dernières frappes du vérificateur sont écrites AVANT la fenêtre de
                     déclaration : à la signature, ses marques sont acceptées dans le fichier
                     (22/10/2026), et une sauvegarde qui arriverait après serait écartée. */
                  if (doc.fichier) void fetchEcriture(`/api/ged/ged/docs/${doc.documentId}/office/flush`, { method: 'POST', credentials: 'include' }).catch(() => {});
                  setDeclarationOuverte(true);
                }}>
                  <I n="shield" s={14} />{occupeRevue('verify') ? 'Signature en cours…' : 'Signer la vérification'}
                </Bouton>
              )}
              {canApprove && derogationRedacteur && (
                <label style={{ display: 'grid', gap: 4, fontSize: 12.5, color: 'var(--text-muted)', borderInlineStart: '3px solid var(--amber-500)', paddingInlineStart: 10 }}>
                  <strong style={{ color: 'var(--text-strong)' }}>Dérogation du responsable qualité — vous avez rédigé ce document</strong>
                  Le document a été vérifié par une autre personne. Dites pourquoi vous l’approuvez vous-même (10 caractères au moins) : le motif figurera au journal signé et sur la version.
                  <textarea value={motifDerogation} onChange={(e) => setMotifDerogation(e.target.value)} rows={2} style={{ ...inputStyle, height: 'auto', padding: '8px 10px' }} />
                </label>
              )}
              {canApprove && (
                <Bouton variante="primaire" onClick={() => void runRevue('approve', async () => { const ok = await act(doc.documentId, 'approve', derogationRedacteur ? { motifDerogation } : undefined); if (ok) go('doc'); })} disabled={occupeRevue() || !relu || (derogationRedacteur && motifDerogation.trim().length < 10)}>
                  <I n="shield" s={14} />{occupeRevue('approve') ? 'Mise en vigueur…' : 'Approuver et mettre en vigueur'}
                </Bouton>
              )}
              {!relu && (
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  La signature s’active une fois le document ouvert en révision.
                </span>
              )}
              {boutonRenvoi}
            </div>
          ) : aRedige ? (
            <div style={{ ...card, padding: '16px 18px', display: 'grid', gap: 10, borderInlineStart: '3px solid var(--amber-500)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>Séparation des tâches — signature impossible</div>
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0, lineHeight: 1.55 }}>
                Vous avez participé à la rédaction de cette version : la norme ISO 15189 §8.3 interdit de
                {isVerify ? ' vérifier' : ' approuver'} son propre travail. Le RQ doit désigner un autre
                {isVerify ? ' vérificateur' : ' approbateur'} depuis la fiche du document.
              </p>
              <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ce que le rédacteur doit reprendre (obligatoire)" rows={3} style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />
              {boutonRenvoi}
            </div>
          ) : (
            <div style={{ ...card, padding: '16px 18px', fontSize: 13, color: 'var(--text-muted)' }}>
              Vous n’avez pas le rôle requis pour signer cette étape.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
